/**
 * VisemePlayer: 音声再生に同期してviseme画像を切り替えるコンポーネント
 * 中間フレーム補間付き
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import type { CSSProperties } from "react";
import { Play } from "lucide-react";
import { loadCanvasImage, parsePercent } from "../lib/canvasImage";
import { preloadImageInfo, preloadImageUrls } from "../lib/imagePreload";
import {
  buildPlaybackIssue,
  describePlaybackIssue,
  hasMediaPlaybackStarted,
  isMediaPlaybackUnlocked,
  markMediaPlaybackUnlocked,
  PlaybackIssueDetails,
  unlockMediaPlayback,
} from "../lib/mediaPlayback";

export interface MappedVisemeEvent {
  timeMs: number;
  viseme: string;
  azureVisemeId?: number;
  dentalPose?: string;
}

export interface JawMotionConfig {
  maskImage: string;
  transformOrigin?: string;
  maxTranslateYPercent?: number;
  maxScaleYDelta?: number;
  maxScaleXDelta?: number;
  transitionMs?: number;
  canvasMouthPatch?: CanvasMouthPatchConfig;
}

export interface CanvasMouthPatchConfig {
  x: number;
  y: number;
  width: number;
  height: number;
  maskCenterYRatio: number;
  maskRadiusXRatio: number;
  maskRadiusYRatio: number;
  featherRatio: number;
}

export interface EyeMotionConfig {
  maskImage?: string;
  maskMode?: "alpha" | "luminance";
  maskPosition: string;
  maskSize: string;
  maxOffsetXPercent: number;
  maxOffsetYPercent: number;
  firstBlinkMinMs?: number;
  firstBlinkMaxMs?: number;
  intervalMinMs?: number;
  intervalMaxMs?: number;
  transitionMs?: number;
  centerReturnChance?: number;
  opacity?: number;
  canvasBlinkMaskUrl?: string;
}

export type VisemeVisualProfile =
  | "balanced"
  | "reducedFlicker"
  | "softBlend"
  | "partialBlend"
  | "anticipatoryBlend"
  | "slowCloseReturn"
  | "vowelHold"
  | "priorityVowelHold"
  | "endingSettle"
  | "transitionFrames"
  | "generatedTransitions";
export type VisemeRenderMode = "dom" | "canvas";
export type VisemePlaybackStatus = "idle" | "playing" | "paused" | "ended";

export interface VisemePlaybackClock {
  timeMs: number;
  durationMs: number | null;
  status: VisemePlaybackStatus;
  playbackRate: number;
}

interface CanvasEyePosition {
  top: string;
  left: string;
  width: string;
  height: string;
}

interface VisemePlayerProps {
  audioUrl: string;
  visemes: MappedVisemeEvent[];
  baseImageUrl: string;
  /** 口ありの元画像（closed時のjawレイヤー用） */
  mouthImageUrl?: string;
  visemeImageUrls: Record<string, string>;
  canonicalPoseImageUrls?: Record<string, string>;
  debug?: boolean;
  runtimePackRoot?: string;
  eyeMotion?: EyeMotionConfig;
  jawMotion?: JawMotionConfig;
  visualLeadMs?: number;
  initialPlaybackDelayMs?: number;
  playbackRateOverride?: number;
  audioMuted?: boolean;
  playbackMode?: "audio" | "clock";
  playbackClockRef?: React.MutableRefObject<VisemePlaybackClock>;
  visualProfile?: VisemeVisualProfile;
  renderMode?: VisemeRenderMode;
  canvasEyePosition?: CanvasEyePosition;
  canvasInternalWidth?: number;
  canvasInternalHeight?: number;
  inlineDebugOverlay?: boolean;
  debugLabel?: string;
  debugConsoleLog?: boolean;
  onDebugSnapshotChange?: (snapshot: VisemeDebugSnapshot | null) => void;
  onPlaybackIssue?: (issue: PlaybackIssueDetails) => void;
  onEnded?: () => void;
}

type TransitionMode = "hold" | "hardCut" | "alpha" | "partial";
type BlinkPhase = "open" | "half_closing" | "closed" | "half_opening";

export interface VisemeDebugSnapshot {
  timeMs: number;
  azureVisemeId?: number;
  dentalPose?: string;
  legacyViseme?: string;
  targetImage: string;
  targetAssetSource: "canonical" | "legacy" | "transition" | "fallback";
  displayImage: string;
  assetSource: "canonical" | "legacy" | "transition" | "fallback";
  assetUrl: string;
  runtimePackRoot?: string;
  transitionMode: TransitionMode;
  jawOpen: number;
  jawTranslateY: number;
  jawScaleY: number;
  playbackRate: number;
  status?: VisemePlaybackStatus;
  transitionLoadedCount?: number;
  transitionPreloadComplete?: boolean;
  canvasImagesReady?: boolean;
  displayQueueLength?: number;
  activeTransitionTarget?: string | null;
  minDisplayFrameMs?: number;
  canvasFps?: number;
  canvasRenderCount?: number;
  canvasRenderDeltaMs?: number;
}

/**
 * viseme間の遷移時に挟む中間フレームを決定
 * closed → a の間に half_a を挟む等
 */
const HALF_FRAMES: Record<string, string> = {
  a: "half_a",
  i: "half_i",
  u: "half_u",
  o: "half_o",
};
const QUARTER_FRAMES: Record<string, string> = {
  a: "quarter_a",
  i: "quarter_i",
  u: "quarter_u",
  o: "quarter_o",
};

const CLOSING_VISEMES = new Set(["closed", "n"]);
const HARD_CUT_VISEMES = new Set(["closed", "n", "o", "u"]);
const SKIP_LEGACY_INTERMEDIATE_VISEMES = new Set(["o", "u"]);
const CANONICAL_TO_LEGACY_VISEME: Record<string, string> = {
  REST: "closed",
  AA: "a",
  E: "e",
  I: "i",
  U: "u",
  O: "o",
  PBM: "closed",
  SZ: "i",
  SH_CH: "u",
  TDN: "n",
  KG: "e",
  FV: "closed",
  L_R: "i",
  DENTAL_OPEN: "a",
};
const SHORT_CLOSED_HOLD_MS = 50; // bilabial閉口をもう少し残す（85→50）
const BILABIAL_ANTICIPATION_RATIO = 0.25; // 閉口の先読み（セグメントの最後25%で閉じ始める）
const BILABIAL_MIN_HOLD_MS = 40; // bilabial後の最低保持時間
const TRANSITION_DURATION_MS = 36;
const TRANSITION_FRAME_PROFILE_STEP_MS = 32;
const IMAGE_CROSSFADE_MS = 45;
const SOFT_BLEND_IMAGE_CROSSFADE_MS = 76;
const SLOW_CLOSE_IMAGE_CROSSFADE_MS = 118;
const VOWEL_HOLD_RELEASE_RATIO = 0.72;
const VOWEL_HOLD_MIN_SEGMENT_MS = 118;
const PRIORITY_VOWEL_PENDING_MAX_MS = 82;
const ENDING_SETTLE_REST_START_MS = 72;
const ENDING_SETTLE_CLOSE_START_MS = 18;
const BALANCED_MIN_DISPLAY_FRAME_MS = 45;
const SOFT_BLEND_MIN_DISPLAY_FRAME_MS = 34;
const DAMPED_VISUAL_TINY_HOLD_MS = 42;
const DAMPED_VISUAL_DELAY_MAX_HOLD_MS = 155;
const DAMPED_VISUAL_SHORT_HOLD_MS = 86;
const DAMPED_VISUAL_SHORT_DELAY_MS = 24;
const DAMPED_VISUAL_DELAY_MS = 38;
const ANTICIPATORY_BLEND_MAX_LEAD_MS = 54;
const ANTICIPATORY_BLEND_MIN_SEGMENT_MS = 92;
const TRANSITION_FRAME_MIN_HOLD_MS = 170;
const TRANSITION_FRAME_PROFILE_MIN_HOLD_MS = 118;
const GENERATED_TRANSITION_FRAME_MIN_HOLD_MS = 68;
const GENERATED_TRANSITION_FRAME_STEP_MS = 17;
const GENERATED_TRANSITION_FRAME_MAX_COUNT = 10;
const GENERATED_TRANSITION_IMAGE_KEY_PREFIX = "transition:";
const GENERATED_TRANSITION_PAIRS = [
  "REST_AA",
  "REST_E",
  "REST_I",
  "REST_U",
  "REST_O",
  "AA_E",
  "AA_I",
  "AA_U",
  "AA_O",
  "E_I",
  "E_U",
  "E_O",
  "I_U",
  "I_O",
  "U_O",
] as const;
const CANVAS_FPS_LOG_INTERVAL_MS = 1000;
const BRIEF_TDN_SKIP_MS = 135;
const BRIEF_CONSONANT_SKIP_MS = 90;
const DEBUG_SNAPSHOT_INTERVAL_MS = 90;
const LONG_HOLD_RELEASE_MIN_MS = 240;
const LONG_HOLD_RELEASE_START_RATIO = 0.46;
const LONG_HOLD_RELEASE_END_RATIO = 0.84;
const LONG_HOLD_JAW_RELAX_START_RATIO = 0.38;
const LONG_HOLD_JAW_RELAX_END_RATIO = 0.82;
const LONG_HOLD_JAW_RELAX_MAX_RATIO = 0.12;
const FINAL_HALF_RELEASE_START_MS = 250;
const FINAL_QUARTER_RELEASE_START_MS = 82;
const FINAL_CLOSE_START_MS = 26;
const JAW_BLEND_START_RATIO = 0.62;

function resolveDebugPlaybackRate(debug: boolean): number {
  if (!debug || typeof window === "undefined") return 1;
  const params = new URLSearchParams(window.location.search);
  const rateParam = Number(params.get("visemeDebugRate"));
  if (Number.isFinite(rateParam) && rateParam >= 0.1 && rateParam <= 1) {
    return rateParam;
  }
  const slowParam = Number(params.get("visemeDebugSlow"));
  if (Number.isFinite(slowParam) && slowParam > 1) {
    return Math.max(0.1, Math.min(1, 1 / slowParam));
  }
  return 1;
}
const JAW_FINAL_RELEASE_MS = 56;
const SPEAKING_BLINK_TIMING = {
  half_closing: 60,
  closed: 90,
  half_opening: 60,
};
const SPEAKING_BLINK_SEQUENCE_MS =
  SPEAKING_BLINK_TIMING.half_closing +
  SPEAKING_BLINK_TIMING.closed +
  SPEAKING_BLINK_TIMING.half_opening;
const SPEAKING_BLINK_END_GUARD_MS = SPEAKING_BLINK_SEQUENCE_MS + 48;
const SPEAKING_BLINK_MASK =
  "radial-gradient(ellipse at center, black 74%, rgba(0,0,0,0.9) 86%, transparent 100%)";
const HEAD_MOTION_MASK =
  "radial-gradient(ellipse 26% 43% at 50% 45%, black 64%, rgba(0,0,0,0.72) 80%, transparent 100%)";
const HEAD_ANIMATION_DURATION_MS = 6000;
const DEFAULT_JAW_MOTION: Required<JawMotionConfig> = {
  maskImage:
    "radial-gradient(ellipse 23% 14% at 50% 68%, black 42%, rgba(0,0,0,0.96) 60%, transparent 84%)",
  transformOrigin: "50% 61%",
  maxTranslateYPercent: 1.5,
  maxScaleYDelta: 0,
  maxScaleXDelta: 0,
  transitionMs: 55,
  canvasMouthPatch: {
    x: 0.34,
    y: 0.53,
    width: 0.32,
    height: 0.27,
    maskCenterYRatio: 0.56,
    maskRadiusXRatio: 0.44,
    maskRadiusYRatio: 0.39,
    featherRatio: 0.055,
  },
};

interface CoverSourceRect {
  sourceX: number;
  sourceY: number;
  sourceWidth: number;
  sourceHeight: number;
}

function getCoverSourceRect(
  image: HTMLImageElement,
  width: number,
  height: number
): CoverSourceRect {
  const imageAspect = image.naturalWidth / image.naturalHeight;
  const canvasAspect = width / height;
  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;

  if (imageAspect > canvasAspect) {
    sourceWidth = image.naturalHeight * canvasAspect;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / canvasAspect;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  return { sourceX, sourceY, sourceWidth, sourceHeight };
}

function drawCoverImage(
  context: CanvasRenderingContext2D,
  image: HTMLImageElement,
  width: number,
  height: number
) {
  const source = getCoverSourceRect(image, width, height);
  context.drawImage(
    image,
    source.sourceX,
    source.sourceY,
    source.sourceWidth,
    source.sourceHeight,
    0,
    0,
    width,
    height
  );
}

function getOrCreateCanvas(
  ref: React.MutableRefObject<HTMLCanvasElement | null>,
  width: number,
  height: number
): HTMLCanvasElement {
  if (!ref.current) {
    ref.current = document.createElement("canvas");
  }
  if (ref.current.width !== width || ref.current.height !== height) {
    ref.current.width = width;
    ref.current.height = height;
  }
  return ref.current;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function splitCssPair(value: string | undefined): [string | undefined, string | undefined] {
  const parts = value?.trim().split(/\s+/).filter(Boolean) ?? [];
  return [parts[0], parts[1]];
}

function parseBlinkSizePercent(value: string | undefined, fallback: number): number {
  if (!value || value.trim() === "100%") return fallback;
  return parsePercent(value, fallback);
}

function getCanvasEyePatchRect(
  width: number,
  height: number,
  eyeMotion: EyeMotionConfig | undefined,
  eyePosition: CanvasEyePosition | undefined,
  expand: boolean
) {
  const [motionLeft, motionTop] = splitCssPair(eyeMotion?.maskPosition);
  const [motionWidth, motionHeight] = splitCssPair(eyeMotion?.maskSize);
  const left = eyePosition
    ? parsePercent(eyePosition.left, 0.398)
    : parsePercent(motionLeft, 0.398);
  const top = eyePosition
    ? parsePercent(eyePosition.top, 0.378)
    : parsePercent(motionTop, 0.378);
  const eyeWidth = eyePosition
    ? parsePercent(eyePosition.width, 0.1625)
    : parseBlinkSizePercent(motionWidth, 0.1625);
  const eyeHeight = eyePosition
    ? parsePercent(eyePosition.height, 0.054)
    : parseBlinkSizePercent(motionHeight, 0.054);
  const padX = expand ? Math.max(0.025, eyeWidth * 0.28) : 0;
  const padTopY = expand ? Math.max(0.07, eyeHeight * 1.25) : 0;
  const padBottomY = expand ? Math.max(0.018, eyeHeight * 0.35) : 0;
  const extraWidth = expand ? Math.max(0.05, eyeWidth * 0.54) : 0;
  const xPct = clamp(left - padX, 0, 1);
  const yPct = clamp(top - padTopY, 0, 1);
  const widthPct = clamp(eyeWidth + extraWidth, 0.01, 1 - xPct);
  const heightPct = clamp(eyeHeight + padTopY + padBottomY, 0.01, 1 - yPct);

  return {
    x: xPct * width,
    y: yPct * height,
    width: widthPct * width,
    height: heightPct * height,
  };
}

function drawFeatheredMouthPatch(
  targetContext: CanvasRenderingContext2D,
  layerCanvas: HTMLCanvasElement,
  image: HTMLImageElement,
  width: number,
  height: number,
  jawOpen: number,
  jawMotion: Required<JawMotionConfig>
) {
  const layerContext = layerCanvas.getContext("2d");
  if (!layerContext) return;

  const roi = jawMotion.canvasMouthPatch;
  const x = roi.x * width;
  const y = roi.y * height;
  const patchWidth = roi.width * width;
  const patchHeight = roi.height * height;
  const source = getCoverSourceRect(image, width, height);
  const sourceX = source.sourceX + roi.x * source.sourceWidth;
  const sourceY = source.sourceY + roi.y * source.sourceHeight;
  const sourceWidth = roi.width * source.sourceWidth;
  const sourceHeight = roi.height * source.sourceHeight;
  const translateY = (jawMotion.maxTranslateYPercent * jawOpen / 100) * height;
  const scaleY = 1 + jawMotion.maxScaleYDelta * jawOpen;

  layerContext.clearRect(0, 0, width, height);
  layerContext.save();
  layerContext.imageSmoothingEnabled = true;
  layerContext.imageSmoothingQuality = "high";
  layerContext.translate(0, translateY);
  layerContext.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    x,
    y,
    patchWidth,
    patchHeight * scaleY
  );
  layerContext.restore();

  layerContext.save();
  layerContext.globalCompositeOperation = "destination-in";
  layerContext.filter = `blur(${Math.max(4, patchHeight * roi.featherRatio)}px)`;
  layerContext.fillStyle = "black";
  layerContext.beginPath();
  layerContext.ellipse(
    x + patchWidth / 2,
    y + patchHeight * roi.maskCenterYRatio,
    patchWidth * roi.maskRadiusXRatio,
    patchHeight * roi.maskRadiusYRatio,
    0,
    0,
    Math.PI * 2
  );
  layerContext.fill();
  layerContext.restore();

  targetContext.drawImage(layerCanvas, 0, 0);
}

function drawCanvasBlinkOverlay(
  context: CanvasRenderingContext2D,
  layerCanvas: HTMLCanvasElement,
  image: HTMLImageElement,
  width: number,
  height: number,
  eyeMotion: EyeMotionConfig | undefined,
  eyePosition: CanvasEyePosition | undefined,
  opacity: number,
  maskImage?: HTMLImageElement | null
) {
  if (opacity <= 0) return;

  if (image.naturalWidth > 500 && maskImage) {
    const layerContext = layerCanvas.getContext("2d");
    if (!layerContext) return;

    layerContext.clearRect(0, 0, width, height);
    layerContext.save();
    layerContext.imageSmoothingEnabled = true;
    layerContext.imageSmoothingQuality = "high";
    drawCoverImage(layerContext, image, width, height);
    layerContext.globalCompositeOperation = "destination-in";
    drawCoverImage(layerContext, maskImage, width, height);
    layerContext.restore();

    context.save();
    context.globalAlpha = opacity;
    context.drawImage(layerCanvas, 0, 0);
    context.restore();
    return;
  }

  context.save();
  context.globalAlpha = opacity;

  if (image.naturalWidth > 500) {
    const rect = getCanvasEyePatchRect(width, height, eyeMotion, eyePosition, true);
    const source = getCoverSourceRect(image, width, height);
    const sourceX = source.sourceX + (rect.x / width) * source.sourceWidth;
    const sourceY = source.sourceY + (rect.y / height) * source.sourceHeight;
    const sourceWidth = (rect.width / width) * source.sourceWidth;
    const sourceHeight = (rect.height / height) * source.sourceHeight;

    context.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      rect.x,
      rect.y,
      rect.width,
      rect.height
    );
  } else {
    const rect = getCanvasEyePatchRect(width, height, eyeMotion, eyePosition, false);
    context.drawImage(image, rect.x, rect.y, rect.width, rect.height);
  }

  context.restore();
}

function resolveDebugJawMotionOverrides(debug: boolean): Partial<JawMotionConfig> {
  if (!debug || typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const overrides: Partial<JawMotionConfig> = {};
  const translateY = Number(params.get("visemeDebugJawY"));
  if (Number.isFinite(translateY) && translateY >= 0 && translateY <= 4) {
    overrides.maxTranslateYPercent = translateY;
  }
  const scaleY = Number(params.get("visemeDebugJawScaleY"));
  if (Number.isFinite(scaleY) && scaleY >= 0 && scaleY <= 0.08) {
    overrides.maxScaleYDelta = scaleY;
  }
  return overrides;
}
/** 開口量の全体係数（1.0=元の値、下げると口の開きが控えめに） */
const JAW_OPEN_SCALE = 1.1;
const JAW_OPEN_BY_VISEME: Record<string, number> = {
  closed: 0,
  n: 0.04 * JAW_OPEN_SCALE,
  smile: 0.06 * JAW_OPEN_SCALE,
  e: 0.18 * JAW_OPEN_SCALE,
  i: 0.15 * JAW_OPEN_SCALE,
  u: 0.3 * JAW_OPEN_SCALE,
  o: 0.34 * JAW_OPEN_SCALE,
  a: 0.42 * JAW_OPEN_SCALE,
  half_a: 0.24 * JAW_OPEN_SCALE,
  half_i: 0.14 * JAW_OPEN_SCALE,
  half_u: 0.2 * JAW_OPEN_SCALE,
  half_o: 0.23 * JAW_OPEN_SCALE,
  quarter_a: 0.13 * JAW_OPEN_SCALE,
  quarter_i: 0.08 * JAW_OPEN_SCALE,
  quarter_u: 0.11 * JAW_OPEN_SCALE,
  quarter_o: 0.12 * JAW_OPEN_SCALE,
};
const PRIORITY_VOWEL_HOLD_VISEMES = new Set(["a", "e", "o", "u"]);

function isSoftBlendProfile(visualProfile: VisemeVisualProfile): boolean {
  return (
    visualProfile === "softBlend" ||
    visualProfile === "partialBlend" ||
    visualProfile === "anticipatoryBlend" ||
    visualProfile === "slowCloseReturn" ||
    visualProfile === "vowelHold" ||
    visualProfile === "priorityVowelHold" ||
    visualProfile === "endingSettle" ||
    visualProfile === "transitionFrames" ||
    visualProfile === "generatedTransitions"
  );
}

function isEndingSettleProfile(visualProfile: VisemeVisualProfile): boolean {
  return (
    visualProfile === "endingSettle" ||
    visualProfile === "transitionFrames" ||
    visualProfile === "generatedTransitions"
  );
}

function isVowelHoldProfile(visualProfile: VisemeVisualProfile): boolean {
  return (
    visualProfile === "vowelHold" ||
    visualProfile === "priorityVowelHold" ||
    isEndingSettleProfile(visualProfile)
  );
}

function isSlowCloseProfile(visualProfile: VisemeVisualProfile): boolean {
  return visualProfile === "slowCloseReturn" || isVowelHoldProfile(visualProfile);
}

function shouldHardCutTransition(
  from: string,
  to: string,
  visualProfile: VisemeVisualProfile
): boolean {
  if (
    visualProfile === "generatedTransitions" &&
    (isGeneratedTransitionImageKey(from) || isGeneratedTransitionImageKey(to))
  ) {
    return true;
  }

  if (isSoftBlendProfile(visualProfile)) {
    return false;
  }
  const fromMotion = motionVisemeName(from);
  const toMotion = motionVisemeName(to);
  return HARD_CUT_VISEMES.has(fromMotion) || HARD_CUT_VISEMES.has(toMotion);
}

function canonicalDisplayKey(pose: string): string {
  return `canonical:${pose}`;
}

function canonicalPoseFromDisplayKey(viseme: string): string | null {
  return viseme.startsWith("canonical:") ? viseme.slice("canonical:".length) : null;
}

function isGeneratedTransitionImageKey(imageKey: string): boolean {
  return imageKey.startsWith(GENERATED_TRANSITION_IMAGE_KEY_PREFIX);
}

function generatedTransitionImageKey(pair: string, index: number): string {
  return `${GENERATED_TRANSITION_IMAGE_KEY_PREFIX}${pair}:${String(index).padStart(2, "0")}`;
}

function parseGeneratedTransitionImageKey(imageKey: string): { pair: string; frame: number } | null {
  if (!isGeneratedTransitionImageKey(imageKey)) return null;
  const [, pair, frameText] = imageKey.match(/^transition:([^:]+):(\d+)$/) ?? [];
  if (!pair || !frameText) return null;
  const frame = Number(frameText);
  return Number.isFinite(frame) ? { pair, frame } : null;
}

function buildGeneratedTransitionCandidates(runtimePackRoot: string): Array<{ key: string; url: string }> {
  return GENERATED_TRANSITION_PAIRS.flatMap((pair) =>
    Array.from({ length: GENERATED_TRANSITION_FRAME_MAX_COUNT }, (_, index) => {
      const frameNumber = index + 1;
      const paddedFrameNumber = String(frameNumber).padStart(2, "0");
      return {
        key: generatedTransitionImageKey(pair, frameNumber),
        url: `${runtimePackRoot}/transition_${pair}_${paddedFrameNumber}.jpg`,
      };
    })
  );
}

function normalizeGeneratedTransitionPose(pose: string): string {
  if (pose === "DENTAL_OPEN") return "AA";
  if (pose === "TDN" || pose === "PBM" || pose === "FV") return "REST";
  if (pose === "SZ" || pose === "L_R") return "I";
  if (pose === "SH_CH") return "U";
  if (pose === "KG") return "E";
  return pose;
}

function motionVisemeName(viseme: string): string {
  const canonicalPose = canonicalPoseFromDisplayKey(viseme);
  return canonicalPose ? CANONICAL_TO_LEGACY_VISEME[canonicalPose] ?? "closed" : viseme;
}

function generatedTransitionPoseFromDisplayKey(imageKey: string): string | null {
  const canonicalPose = canonicalPoseFromDisplayKey(imageKey);
  if (canonicalPose) return normalizeGeneratedTransitionPose(canonicalPose);

  switch (motionVisemeName(imageKey)) {
    case "closed":
    case "smile":
      return "REST";
    case "a":
      return "AA";
    case "e":
      return "E";
    case "i":
      return "I";
    case "u":
      return "U";
    case "o":
      return "O";
    case "n":
      return "TDN";
    default:
      return null;
  }
}

function generatedTransitionPairKey(from: string, to: string): string | null {
  const fromPose = generatedTransitionPoseFromDisplayKey(from);
  const toPose = generatedTransitionPoseFromDisplayKey(to);
  if (!fromPose || !toPose || fromPose === toPose) return null;
  return `${fromPose}_${toPose}`;
}

function getGeneratedTransitionFramePlan(
  from: string,
  to: string
): { pair: string; reverse: boolean; normalizedPair: string } | null {
  const normalizedPair = generatedTransitionPairKey(from, to);
  if (!normalizedPair) return null;

  if ((GENERATED_TRANSITION_PAIRS as readonly string[]).includes(normalizedPair)) {
    return { pair: normalizedPair, reverse: false, normalizedPair };
  }

  const [fromPose, toPose] = normalizedPair.split("_");
  const reversePair = `${toPose}_${fromPose}`;
  if ((GENERATED_TRANSITION_PAIRS as readonly string[]).includes(reversePair)) {
    return { pair: reversePair, reverse: true, normalizedPair };
  }

  return null;
}

function eventMotionVisemeName(
  event: MappedVisemeEvent,
  canonicalPoseImageUrls?: Record<string, string>
): string {
  return event.dentalPose && canonicalPoseImageUrls?.[event.dentalPose]
    ? CANONICAL_TO_LEGACY_VISEME[event.dentalPose] ?? event.viseme
    : event.viseme;
}

function eventIdentityKey(event: MappedVisemeEvent): string {
  return event.dentalPose ?? event.viseme;
}

function eventDentalPoseName(event?: MappedVisemeEvent | null): string {
  return event?.dentalPose ?? "";
}

function shouldSkipJapaneseFillerI(
  previous: MappedVisemeEvent,
  current: MappedVisemeEvent,
  next: MappedVisemeEvent,
  holdMs: number
): boolean {
  if (eventDentalPoseName(current) !== "I") return false;

  const previousPose = eventDentalPoseName(previous);
  const nextPose = eventDentalPoseName(next);
  const isBrief = holdMs <= 150;

  if (previousPose === "SZ" && nextPose === "REST") {
    return true;
  }

  if (isBrief && previousPose === "U" && (nextPose === "TDN" || nextPose === "PBM" || nextPose === "REST")) {
    return true;
  }

  if (isBrief && previousPose === "PBM" && (nextPose === "TDN" || nextPose === "E" || nextPose === "REST")) {
    return true;
  }

  if (isBrief && previousPose === "SH_CH" && (nextPose === "O" || nextPose === "U")) {
    return true;
  }

  return false;
}

function shouldSkipBriefVisualPose(
  previous: MappedVisemeEvent,
  current: MappedVisemeEvent,
  next: MappedVisemeEvent,
  holdMs: number
): boolean {
  const currentPose = eventDentalPoseName(current);
  const previousPose = eventDentalPoseName(previous);
  const nextPose = eventDentalPoseName(next);

  if (
    currentPose === "TDN" &&
    holdMs <= BRIEF_TDN_SKIP_MS &&
    nextPose !== "REST" &&
    nextPose !== "PBM"
  ) {
    return true;
  }

  if (
    holdMs <= BRIEF_CONSONANT_SKIP_MS &&
    (currentPose === "KG" || currentPose === "FV" || currentPose === "L_R" || currentPose === "SH_CH") &&
    previousPose !== "REST" &&
    nextPose !== "REST"
  ) {
    return true;
  }

  if (
    holdMs <= BRIEF_CONSONANT_SKIP_MS &&
    currentPose === "SZ" &&
    previousPose !== "REST" &&
    (nextPose === "I" || nextPose === "REST")
  ) {
    return true;
  }

  return false;
}

function displayVisemeName(event: MappedVisemeEvent, canonicalPoseImageUrls?: Record<string, string>): string {
  if (event.dentalPose && canonicalPoseImageUrls?.[event.dentalPose]) {
    return canonicalDisplayKey(event.dentalPose);
  }
  return event.viseme;
}

function assetSourceForDisplayKey(
  imageKey: string,
  visemeImageUrls: Record<string, string>,
  canonicalPoseImageUrls?: Record<string, string>
): VisemeDebugSnapshot["assetSource"] {
  if (isGeneratedTransitionImageKey(imageKey)) {
    return "transition";
  }
  const canonicalPose = canonicalPoseFromDisplayKey(imageKey);
  if (canonicalPose) {
    return canonicalPoseImageUrls?.[canonicalPose] ? "canonical" : "fallback";
  }
  return imageKey === "closed" || visemeImageUrls[imageKey] ? "legacy" : "fallback";
}

function getTransitionFrame(from: string, to: string): string | null {
  // 同じvisemeなら補間不要
  if (from === to) return null;
  if (shouldHardCutTransition(from, to, "balanced")) return null;
  const fromMotion = motionVisemeName(from);
  const toMotion = motionVisemeName(to);

  // closedから母音へ → half版を挟む
  if (fromMotion === "closed" || fromMotion === "n" || fromMotion === "smile") {
    return HALF_FRAMES[toMotion] || null;
  }

  // 母音からclosedへ → 元の母音のhalf版を挟む
  if (toMotion === "closed" || toMotion === "n") {
    return HALF_FRAMES[fromMotion] || QUARTER_FRAMES[fromMotion] || null;
  }

  // 母音→母音の遷移 → 行き先のhalf版を挟む
  return HALF_FRAMES[toMotion] || null;
}

function existingTransitionFrames(
  frames: Array<string | null | undefined>,
  visemeImageUrls: Record<string, string>
): string[] {
  return frames.filter((frame): frame is string => !!frame && !!visemeImageUrls[frame]);
}

function getTransitionFrameSequence(
  from: string,
  to: string,
  visemeImageUrls: Record<string, string>
): string[] {
  if (from === to) return [];

  const fromMotion = motionVisemeName(from);
  const toMotion = motionVisemeName(to);
  const fromIsClosing = CLOSING_VISEMES.has(fromMotion) || fromMotion === "smile";
  const toIsClosing = CLOSING_VISEMES.has(toMotion);

  if (fromIsClosing && !toIsClosing) {
    return existingTransitionFrames(
      [QUARTER_FRAMES[toMotion], HALF_FRAMES[toMotion]],
      visemeImageUrls
    );
  }

  if (!fromIsClosing && toIsClosing) {
    return existingTransitionFrames(
      [HALF_FRAMES[fromMotion], QUARTER_FRAMES[fromMotion]],
      visemeImageUrls
    );
  }

  if (!fromIsClosing && !toIsClosing) {
    return existingTransitionFrames([HALF_FRAMES[toMotion]], visemeImageUrls);
  }

  return [];
}

function getGeneratedTransitionFrameCount(holdMs: number | null): number {
  if (!holdMs || !Number.isFinite(holdMs) || holdMs < GENERATED_TRANSITION_FRAME_MIN_HOLD_MS) {
    return 0;
  }

  const frameSlots = Math.floor(holdMs / GENERATED_TRANSITION_FRAME_STEP_MS) - 1;
  return Math.max(2, Math.min(GENERATED_TRANSITION_FRAME_MAX_COUNT, frameSlots));
}

function pickEvenlyDistributedFrames<T>(frames: T[], count: number): T[] {
  if (count <= 0) return [];
  if (count >= frames.length) return frames;

  const selectedIndexes: number[] = [];
  const used = new Set<number>();
  const maxIndex = frames.length - 1;

  for (let i = 0; i < count; i += 1) {
    let index = Math.round(((i + 1) * (frames.length + 1)) / (count + 1)) - 1;
    index = clamp(index, 0, maxIndex);

    if (used.has(index)) {
      let forward = index + 1;
      let backward = index - 1;
      while (forward <= maxIndex || backward >= 0) {
        if (forward <= maxIndex && !used.has(forward)) {
          index = forward;
          break;
        }
        if (backward >= 0 && !used.has(backward)) {
          index = backward;
          break;
        }
        forward += 1;
        backward -= 1;
      }
    }

    used.add(index);
    selectedIndexes.push(index);
  }

  return selectedIndexes.sort((a, b) => a - b).map((index) => frames[index]);
}

function getGeneratedTransitionFrameSequence(
  from: string,
  to: string,
  visemeWindow: VisemeWindow | null,
  generatedTransitionImageUrls: Record<string, string>
): string[] {
  const plan = getGeneratedTransitionFramePlan(from, to);
  if (!plan) return [];

  const holdMs = visemeWindow?.next
    ? visemeWindow.next.timeMs - visemeWindow.current.timeMs
    : null;
  const frameCount = getGeneratedTransitionFrameCount(holdMs);
  if (frameCount === 0) return [];

  const orderedFrames = Array.from({ length: GENERATED_TRANSITION_FRAME_MAX_COUNT }, (_, index) =>
    generatedTransitionImageKey(plan.pair, index + 1)
  );
  const availableFrames = (plan.reverse ? orderedFrames.reverse() : orderedFrames)
    .filter((key) => !!generatedTransitionImageUrls[key]);

  return pickEvenlyDistributedFrames(availableFrames, Math.min(frameCount, availableFrames.length));
}

function getVisualProfileMinDisplayFrameMs(visualProfile: VisemeVisualProfile): number {
  if (visualProfile === "generatedTransitions") {
    return GENERATED_TRANSITION_FRAME_STEP_MS;
  }

  return isSoftBlendProfile(visualProfile)
    ? SOFT_BLEND_MIN_DISPLAY_FRAME_MS
    : BALANCED_MIN_DISPLAY_FRAME_MS;
}

function getVisualProfileCrossfadeMs(visualProfile: VisemeVisualProfile): number {
  return isSoftBlendProfile(visualProfile)
    ? SOFT_BLEND_IMAGE_CROSSFADE_MS
    : IMAGE_CROSSFADE_MS;
}

function isClosingDisplayKey(imageKey: string): boolean {
  return CLOSING_VISEMES.has(motionVisemeName(imageKey));
}

function getTransitionCrossfadeMs(
  from: string,
  to: string,
  visualProfile: VisemeVisualProfile
): number {
  if (
    isSlowCloseProfile(visualProfile) &&
    isClosingDisplayKey(to) &&
    !isClosingDisplayKey(from)
  ) {
    return SLOW_CLOSE_IMAGE_CROSSFADE_MS;
  }

  return getVisualProfileCrossfadeMs(visualProfile);
}

function getTransitionFrames(
  from: string,
  to: string,
  visemeWindow: VisemeWindow | null,
  visualProfile: VisemeVisualProfile,
  visemeImageUrls: Record<string, string>,
  generatedTransitionImageUrls: Record<string, string> = {}
): string[] {
  if (visualProfile === "generatedTransitions") {
    const generatedFrames = getGeneratedTransitionFrameSequence(
      from,
      to,
      visemeWindow,
      generatedTransitionImageUrls
    );
    if (generatedFrames.length > 0) return generatedFrames;

    const fallbackFrames = getTransitionFrameSequence(from, to, visemeImageUrls);
    return shouldUseTransitionFrame(to, visemeWindow, "transitionFrames")
      ? fallbackFrames
      : [];
  }

  if (
    isSoftBlendProfile(visualProfile) &&
    visualProfile !== "transitionFrames"
  ) {
    return [];
  }

  if (visualProfile === "transitionFrames") {
    const transitionFrames = getTransitionFrameSequence(from, to, visemeImageUrls);
    return shouldUseTransitionFrame(to, visemeWindow, visualProfile)
      ? transitionFrames
      : [];
  }

  const transitionFrame = getTransitionFrame(from, to);
  if (
    transitionFrame &&
    visemeImageUrls[transitionFrame] &&
    shouldUseTransitionFrame(to, visemeWindow, visualProfile)
  ) {
    return [transitionFrame];
  }

  return [];
}

function shouldDeferShortVisualTarget(
  visemeWindow: VisemeWindow | null,
  baseViseme: string,
  displayedImage: string,
  timeMs: number
): boolean {
  if (!visemeWindow?.next) return false;
  if (baseViseme === displayedImage) return false;
  if (eventDentalPoseName(visemeWindow.current) === "REST") return false;

  const holdMs = visemeWindow.next.timeMs - visemeWindow.current.timeMs;
  if (holdMs <= 0 || holdMs > DAMPED_VISUAL_DELAY_MAX_HOLD_MS) return false;

  const elapsedMs = timeMs - visemeWindow.current.timeMs;
  if (elapsedMs < 0 || elapsedMs > holdMs) return false;

  if (holdMs <= DAMPED_VISUAL_TINY_HOLD_MS) return true;

  const delayMs = holdMs <= DAMPED_VISUAL_SHORT_HOLD_MS
    ? Math.min(DAMPED_VISUAL_SHORT_DELAY_MS, holdMs * 0.35)
    : Math.min(DAMPED_VISUAL_DELAY_MS, holdMs * 0.38);

  return elapsedMs < delayMs;
}

function shouldAnticipateNextViseme(
  visemeWindow: VisemeWindow | null,
  timeMs: number
): boolean {
  if (!visemeWindow?.next) return false;
  if (eventIdentityKey(visemeWindow.current) === eventIdentityKey(visemeWindow.next)) return false;

  const segmentMs = visemeWindow.next.timeMs - visemeWindow.current.timeMs;
  if (segmentMs < ANTICIPATORY_BLEND_MIN_SEGMENT_MS) return false;

  const remainingMs = visemeWindow.next.timeMs - timeMs;
  if (remainingMs <= 0) return false;

  const leadMs = Math.min(ANTICIPATORY_BLEND_MAX_LEAD_MS, segmentMs * 0.42);
  return remainingMs <= leadMs;
}

function shouldHoldCurrentVowel(
  visemeWindow: VisemeWindow | null,
  timeMs: number,
  canonicalPoseImageUrls?: Record<string, string>
): boolean {
  if (!visemeWindow?.next) return false;

  const currentMotion = eventMotionVisemeName(visemeWindow.current, canonicalPoseImageUrls);
  const nextMotion = eventMotionVisemeName(visemeWindow.next, canonicalPoseImageUrls);
  if (!HALF_FRAMES[currentMotion]) return false;
  if (currentMotion === nextMotion) return false;

  const segmentMs = visemeWindow.next.timeMs - visemeWindow.current.timeMs;
  if (segmentMs < VOWEL_HOLD_MIN_SEGMENT_MS) return false;

  const elapsedMs = timeMs - visemeWindow.current.timeMs;
  if (elapsedMs < 0 || elapsedMs > segmentMs) return false;

  return elapsedMs / segmentMs < VOWEL_HOLD_RELEASE_RATIO;
}

function isPriorityVowelDisplayKey(imageKey: string): boolean {
  return PRIORITY_VOWEL_HOLD_VISEMES.has(motionVisemeName(imageKey));
}

function normalizeVisemeSequence(
  events: MappedVisemeEvent[],
  visualProfile: VisemeVisualProfile
): MappedVisemeEvent[] {
  if (!events || events.length === 0) return [];

  const sorted = [...events].sort((a, b) => a.timeMs - b.timeMs);
  const deduped: MappedVisemeEvent[] = [];

  for (const event of sorted) {
    const prev = deduped[deduped.length - 1];

    if (prev && prev.timeMs === event.timeMs) {
      deduped[deduped.length - 1] = event;
      continue;
    }

    if (prev && eventIdentityKey(prev) === eventIdentityKey(event)) {
      continue;
    }

    deduped.push(event);
  }

  if (deduped.length < 3) return deduped;

  const smoothed: MappedVisemeEvent[] = [deduped[0]];

  for (let i = 1; i < deduped.length - 1; i += 1) {
    const current = deduped[i];
    const previous = smoothed[smoothed.length - 1];
    const next = deduped[i + 1];
    const holdMs = next.timeMs - current.timeMs;

    const shouldSkipBriefClosure =
      current.dentalPose !== "PBM" &&
      current.dentalPose !== "TDN" &&
      CLOSING_VISEMES.has(current.viseme) &&
      !CLOSING_VISEMES.has(previous.viseme) &&
      !CLOSING_VISEMES.has(next.viseme) &&
      holdMs <= SHORT_CLOSED_HOLD_MS;

    if (shouldSkipBriefClosure) {
      continue;
    }

    if (shouldSkipJapaneseFillerI(previous, current, next, holdMs)) {
      continue;
    }

    if (visualProfile === "reducedFlicker" && shouldSkipBriefVisualPose(previous, current, next, holdMs)) {
      continue;
    }

    smoothed.push(current);
  }

  smoothed.push(deduped[deduped.length - 1]);
  return smoothed;
}

function computeHeadAnimationDelayMs() {
  if (typeof performance === "undefined") return 0;
  return -Math.floor(performance.now() % HEAD_ANIMATION_DURATION_MS);
}

function waitForAudioCanPlay(audio: HTMLAudioElement): Promise<void> {
  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const cleanup = () => {
      audio.removeEventListener("canplay", handleReady);
      audio.removeEventListener("loadeddata", handleReady);
      audio.removeEventListener("error", handleReady);
    };
    const handleReady = () => {
      cleanup();
      resolve();
    };

    audio.addEventListener("canplay", handleReady, { once: true });
    audio.addEventListener("loadeddata", handleReady, { once: true });
    audio.addEventListener("error", handleReady, { once: true });
  });
}

interface VisemeWindow {
  prev: MappedVisemeEvent;
  current: MappedVisemeEvent;
  next: MappedVisemeEvent | null;
}

function getSustainedHoldFrame(
  baseViseme: string,
  current: MappedVisemeEvent,
  next: MappedVisemeEvent | null,
  timeMs: number
) {
  if (!next) return null;
  if (canonicalPoseFromDisplayKey(baseViseme)) return null;
  const motionViseme = motionVisemeName(baseViseme);
  if (CLOSING_VISEMES.has(motionViseme)) return null;
  if (SKIP_LEGACY_INTERMEDIATE_VISEMES.has(motionViseme)) return null;

  const halfFrame = HALF_FRAMES[motionViseme];
  if (!halfFrame) return null;

  const holdMs = next.timeMs - current.timeMs;
  if (holdMs < LONG_HOLD_RELEASE_MIN_MS) return null;

  const elapsed = timeMs - current.timeMs;
  const releaseStartMs = holdMs * LONG_HOLD_RELEASE_START_RATIO;
  const releaseEndMs = holdMs * LONG_HOLD_RELEASE_END_RATIO;

  if (elapsed >= releaseStartMs && elapsed <= releaseEndMs) {
    return halfFrame;
  }

  return null;
}

function getFinalReleaseFrame(
  baseViseme: string,
  durationMs: number | null,
  timeMs: number,
  visualProfile: VisemeVisualProfile
) {
  if (!durationMs || !Number.isFinite(durationMs)) return null;
  const canonicalPose = canonicalPoseFromDisplayKey(baseViseme);
  const motionViseme = motionVisemeName(baseViseme);
  if (CLOSING_VISEMES.has(motionViseme)) return null;

  const remainingMs = durationMs - timeMs;
  if (remainingMs <= 0) return "closed";
  if (isEndingSettleProfile(visualProfile)) {
    if (remainingMs <= ENDING_SETTLE_CLOSE_START_MS) {
      return canonicalPose ? "canonical:REST" : "closed";
    }
    if (remainingMs <= ENDING_SETTLE_REST_START_MS) {
      if (visualProfile === "transitionFrames" || visualProfile === "generatedTransitions") {
        return QUARTER_FRAMES[motionViseme] ?? HALF_FRAMES[motionViseme] ?? (canonicalPose ? "canonical:REST" : "closed");
      }
      if (canonicalPose) return canonicalPose === "REST" ? null : "canonical:REST";
      if (SKIP_LEGACY_INTERMEDIATE_VISEMES.has(motionViseme)) return null;
      return QUARTER_FRAMES[motionViseme] ?? HALF_FRAMES[motionViseme] ?? "closed";
    }

    return null;
  }

  if (remainingMs <= FINAL_CLOSE_START_MS) return "closed";
  if (canonicalPose) return null;
  if (SKIP_LEGACY_INTERMEDIATE_VISEMES.has(motionViseme)) return null;
  if (remainingMs <= FINAL_QUARTER_RELEASE_START_MS) {
    return QUARTER_FRAMES[motionViseme] ?? HALF_FRAMES[motionViseme] ?? "closed";
  }
  if (remainingMs <= FINAL_HALF_RELEASE_START_MS) {
    return HALF_FRAMES[motionViseme] ?? "closed";
  }

  return null;
}

function shouldUseTransitionFrame(
  baseViseme: string,
  visemeWindow: VisemeWindow | null,
  visualProfile: VisemeVisualProfile
): boolean {
  if (visualProfile === "balanced") return true;
  if (!visemeWindow?.next) return false;
  if (
    visualProfile !== "transitionFrames" &&
    visualProfile !== "generatedTransitions" &&
    canonicalPoseFromDisplayKey(baseViseme)
  ) return false;
  const minHoldMs = visualProfile === "transitionFrames" || visualProfile === "generatedTransitions"
    ? TRANSITION_FRAME_PROFILE_MIN_HOLD_MS
    : TRANSITION_FRAME_MIN_HOLD_MS;
  return visemeWindow.next.timeMs - visemeWindow.current.timeMs >= minHoldMs;
}

function easeInOut(progress: number) {
  const clamped = Math.max(0, Math.min(1, progress));
  return clamped * clamped * (3 - 2 * clamped);
}

function easeOutCubic(progress: number) {
  const clamped = Math.max(0, Math.min(1, progress));
  return 1 - Math.pow(1 - clamped, 3);
}

function easePulse(progress: number) {
  const clamped = Math.max(0, Math.min(1, progress));
  return Math.sin(clamped * Math.PI);
}

function getJawOpenForWindow(
  visemeWindow: VisemeWindow | null,
  timeMs: number,
  durationMs: number | null,
  canonicalPoseImageUrls?: Record<string, string>
) {
  if (!visemeWindow) return 0;

  // 3点ブレンド: prev(15%) + current(55%) + next(30%) で滑らかに
  const prevOpen = JAW_OPEN_BY_VISEME[eventMotionVisemeName(visemeWindow.prev, canonicalPoseImageUrls)] ?? 0;
  const currentMotionViseme = eventMotionVisemeName(visemeWindow.current, canonicalPoseImageUrls);
  const rawCurrentOpen = JAW_OPEN_BY_VISEME[currentMotionViseme] ?? 0;
  const rawNextOpen = visemeWindow.next
    ? (JAW_OPEN_BY_VISEME[eventMotionVisemeName(visemeWindow.next, canonicalPoseImageUrls)] ?? rawCurrentOpen)
    : rawCurrentOpen;

  // bilabial（M/B/P=closed/n）は閉口を優先するのでブレンドを弱める
  const isBilabial = CLOSING_VISEMES.has(currentMotionViseme);
  const currentOpen = isBilabial
    ? rawCurrentOpen
    : 0.15 * prevOpen + 0.55 * rawCurrentOpen + 0.30 * rawNextOpen;

  if (durationMs && Number.isFinite(durationMs)) {
    const remainingMs = durationMs - timeMs;
    if (remainingMs <= JAW_FINAL_RELEASE_MS) {
      const releaseProgress = 1 - remainingMs / JAW_FINAL_RELEASE_MS;
      return currentOpen * (1 - easeOutCubic(releaseProgress));
    }
  }

  const next = visemeWindow.next;
  if (!next) return currentOpen;

  const segmentMs = next.timeMs - visemeWindow.current.timeMs;
  if (segmentMs <= 0) {
    return JAW_OPEN_BY_VISEME[eventMotionVisemeName(next, canonicalPoseImageUrls)] ?? currentOpen;
  }

  const progress = (timeMs - visemeWindow.current.timeMs) / segmentMs;

  // bilabial先読み: 次が閉口visemeなら、セグメントの最後で閉じ始める
  if (CLOSING_VISEMES.has(eventMotionVisemeName(next, canonicalPoseImageUrls)) && !isBilabial && progress >= (1 - BILABIAL_ANTICIPATION_RATIO)) {
    const anticipationProgress = (progress - (1 - BILABIAL_ANTICIPATION_RATIO)) / BILABIAL_ANTICIPATION_RATIO;
    return currentOpen * (1 - easeInOut(anticipationProgress));
  }

  // bilabial保持: 現在が閉口visemeで、開始からBILABIAL_MIN_HOLD_MSは閉口を維持
  if (isBilabial) {
    const elapsedMs = timeMs - visemeWindow.current.timeMs;
    if (elapsedMs < BILABIAL_MIN_HOLD_MS) {
      return 0;
    }
  }
  if (!CLOSING_VISEMES.has(currentMotionViseme) && segmentMs >= LONG_HOLD_RELEASE_MIN_MS) {
    const relaxRange =
      LONG_HOLD_JAW_RELAX_END_RATIO - LONG_HOLD_JAW_RELAX_START_RATIO;
    if (progress >= LONG_HOLD_JAW_RELAX_START_RATIO && progress <= LONG_HOLD_JAW_RELAX_END_RATIO) {
      const relaxProgress = (progress - LONG_HOLD_JAW_RELAX_START_RATIO) / relaxRange;
      const relaxAmount = currentOpen * LONG_HOLD_JAW_RELAX_MAX_RATIO * easePulse(relaxProgress);
      return Math.max(0, currentOpen - relaxAmount);
    }
  }

  if (progress <= JAW_BLEND_START_RATIO) {
    return currentOpen;
  }

  const nextOpen = JAW_OPEN_BY_VISEME[eventMotionVisemeName(next, canonicalPoseImageUrls)] ?? currentOpen;
  const blendProgress = easeInOut(
    (progress - JAW_BLEND_START_RATIO) / (1 - JAW_BLEND_START_RATIO)
  );

  return currentOpen + (nextOpen - currentOpen) * blendProgress;
}

const VisemePlayer: React.FC<VisemePlayerProps> = ({
  audioUrl,
  visemes,
  baseImageUrl,
  mouthImageUrl,
  visemeImageUrls,
  canonicalPoseImageUrls: canonicalPoseImageUrlsProp,
  debug = false,
  runtimePackRoot,
  eyeMotion,
  jawMotion,
  visualLeadMs = 0,
  initialPlaybackDelayMs = 0,
  playbackRateOverride,
  audioMuted = false,
  playbackMode = "audio",
  playbackClockRef,
  visualProfile = "endingSettle",
  renderMode = "dom",
  canvasEyePosition,
  canvasInternalWidth = 960,
  canvasInternalHeight = 540,
  inlineDebugOverlay = true,
  debugLabel,
  debugConsoleLog = false,
  onDebugSnapshotChange,
  onPlaybackIssue,
  onEnded,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const reusableAudioRef = useRef<HTMLAudioElement | null>(null);
  const animFrameRef = useRef<number>(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasMouthLayerRef = useRef<HTMLCanvasElement | null>(null);
  const canvasBlinkLayerRef = useRef<HTMLCanvasElement | null>(null);
  const canvasImagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const generatedTransitionImageUrlsRef = useRef<Record<string, string>>({});
  const blinkPhaseRef = useRef<BlinkPhase>("open");
  const playbackStartTimerRef = useRef<number>(0);
  const prevBaseVisemeRef = useRef<string>("closed");
  const displayImageRef = useRef<string>("closed");
  const displayQueueRef = useRef<string[]>([]);
  const displayQueueFirstQueuedAtRef = useRef<number>(0);
  const minDisplayTimerRef = useRef<number>(0);
  const lastDisplayChangeAtRef = useRef<number>(0);
  const transitionTimeoutRef = useRef<number>(0);
  const activeTransitionTargetRef = useRef<string | null>(null);
  const crossfadeTimeoutRef = useRef<number>(0);
  const crossfadeFrameRef = useRef<number>(0);
  const lastDebugKeyRef = useRef<string>("");
  const lastDebugUpdateAtRef = useRef<number>(0);
  const lastDebugTargetRef = useRef<string>("");
  const lastDebugDisplayRef = useRef<string>("");
  const lastLoggedTargetRef = useRef<string>("");
  const lastTransitionModeRef = useRef<TransitionMode>("hold");
  const lastVisualTimeMsRef = useRef<number>(0);
  const lastPlaybackStatusRef = useRef<VisemePlaybackStatus>("idle");
  const canvasRenderCountRef = useRef<number>(0);
  const canvasFpsWindowStartedAtRef = useRef<number>(0);
  const canvasLastRenderAtRef = useRef<number>(0);
  const canvasLastSkipLogAtRef = useRef<number>(0);
  const canvasLastFpsRef = useRef<number>(0);
  const canvasLastRenderDeltaMsRef = useRef<number>(0);
  const canvasTotalRenderCountRef = useRef<number>(0);
  const loggedMissingCanvasImagesRef = useRef<Set<string>>(new Set());
  const jawLayerRef = useRef<HTMLDivElement | null>(null);
  const halfBlinkRef = useRef<HTMLImageElement | null>(null);
  const closedBlinkRef = useRef<HTMLImageElement | null>(null);
  const blinkTimerRef = useRef<number>(0);
  const blinkSequenceRef = useRef<number>(0);
  const blinkResumeAtRef = useRef<number>(0);
  const playbackStartWatchdogRef = useRef<number>(0);
  const jawOpenValueRef = useRef<number>(0);
  const visemeWindowIndexRef = useRef<number>(0);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const onPlaybackIssueRef = useRef(onPlaybackIssue);
  onPlaybackIssueRef.current = onPlaybackIssue;
  const onDebugSnapshotChangeRef = useRef(onDebugSnapshotChange);
  onDebugSnapshotChangeRef.current = onDebugSnapshotChange;
  const [displayImage, setDisplayImage] = useState<string>("closed");
  const [fadeFromImage, setFadeFromImage] = useState<string | null>(null);
  const [debugSnapshot, setDebugSnapshot] = useState<VisemeDebugSnapshot | null>(null);
  useEffect(() => {
    onDebugSnapshotChangeRef.current?.(debugSnapshot);
  }, [debugSnapshot]);
  const [displayOpacity, setDisplayOpacity] = useState<number>(1);
  const [displayTransitionMs, setDisplayTransitionMs] = useState<number>(() =>
    getVisualProfileCrossfadeMs(visualProfile)
  );
  const [failedCanonicalImageUrls, setFailedCanonicalImageUrls] = useState<Set<string>>(() => new Set());
  const [generatedTransitionImageUrls, setGeneratedTransitionImageUrls] = useState<Record<string, string>>({});
  const [generatedTransitionPreloadComplete, setGeneratedTransitionPreloadComplete] = useState(false);
  const [playbackIssue, setPlaybackIssue] = useState<PlaybackIssueDetails | null>(null);
  const [headAnimationDelayMs] = useState(() => computeHeadAnimationDelayMs());
  const normalizedVisemes = useMemo(() => normalizeVisemeSequence(visemes, visualProfile), [visemes, visualProfile]);
  const canonicalPoseImageUrls = useMemo(() => {
    if (!canonicalPoseImageUrlsProp) return undefined;
    if (failedCanonicalImageUrls.size === 0) return canonicalPoseImageUrlsProp;

    const availableUrls = Object.fromEntries(
      Object.entries(canonicalPoseImageUrlsProp).filter(([, url]) => !failedCanonicalImageUrls.has(url))
    );
    return Object.keys(availableUrls).length > 0 ? availableUrls : undefined;
  }, [canonicalPoseImageUrlsProp, failedCanonicalImageUrls]);

  const reportAudioPlaybackIssue = useCallback((reason: string, error?: unknown) => {
    const issue = buildPlaybackIssue(reason, "audio", audioRef.current, error);
    setPlaybackIssue(issue);
    console.error("[VisemePlayer] Audio playback issue:", issue);
    onPlaybackIssueRef.current?.(issue);
  }, []);

  const retryAudioPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      reportAudioPlaybackIssue("response-media-missing");
      return;
    }

    setPlaybackIssue(null);
    if (audio.error || audio.readyState === 0) {
      audio.load();
    }
    audio.currentTime = 0;
    audio.play().then(() => {
      markMediaPlaybackUnlocked();
    }).catch((error) => {
      reportAudioPlaybackIssue("manual-retry-failed", error);
    });
  }, [reportAudioPlaybackIssue]);

  useEffect(() => {
    if (!reusableAudioRef.current) {
      reusableAudioRef.current = new Audio();
    }

    const unlockReusableAudio = () => {
      if (isMediaPlaybackUnlocked()) return;
      void unlockMediaPlayback([reusableAudioRef.current]);
    };

    window.addEventListener("pointerdown", unlockReusableAudio, { capture: true });
    window.addEventListener("touchstart", unlockReusableAudio, { capture: true });
    window.addEventListener("keydown", unlockReusableAudio, { capture: true });
    return () => {
      window.removeEventListener("pointerdown", unlockReusableAudio, { capture: true });
      window.removeEventListener("touchstart", unlockReusableAudio, { capture: true });
      window.removeEventListener("keydown", unlockReusableAudio, { capture: true });
      const audio = reusableAudioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute("src");
        audio.load();
      }
      reusableAudioRef.current = null;
      if (audioRef.current === audio) {
        audioRef.current = null;
      }
    };
  }, []);
  const jawMotionConfig = useMemo(
    () => ({ ...DEFAULT_JAW_MOTION, ...(jawMotion ?? {}), ...resolveDebugJawMotionOverrides(debug) }),
    [debug, jawMotion]
  );
  const debugPlaybackRate = useMemo(() => {
    if (Number.isFinite(playbackRateOverride) && playbackRateOverride && playbackRateOverride > 0) {
      return Math.max(0.1, Math.min(1, playbackRateOverride));
    }
    return resolveDebugPlaybackRate(debug);
  }, [debug, playbackRateOverride]);
  const debugLogName = debugLabel ?? visualProfile;
  const debugLog = useCallback((event: string, payload: Record<string, unknown> = {}) => {
    if (!debug || !debugConsoleLog) return;
    const entry = {
      event,
      timeMs: Math.round(lastVisualTimeMsRef.current),
      status: lastPlaybackStatusRef.current,
      display: displayImageRef.current,
      target: lastDebugTargetRef.current || prevBaseVisemeRef.current,
      queueLength: displayQueueRef.current.length,
      activeTarget: activeTransitionTargetRef.current,
      ...payload,
    };
    const debugWindow = typeof window !== "undefined" ? (window as any) : null;
    if (debugWindow) {
      debugWindow.__VISEME_DEBUG_LOGS = Array.isArray(debugWindow.__VISEME_DEBUG_LOGS)
        ? debugWindow.__VISEME_DEBUG_LOGS
        : [];
      debugWindow.__VISEME_DEBUG_LOGS.push(entry);
      debugWindow.__VISEME_DEBUG_DUMP = () => JSON.stringify(debugWindow.__VISEME_DEBUG_LOGS, null, 2);
      debugWindow.__VISEME_DEBUG_CLEAR = () => {
        debugWindow.__VISEME_DEBUG_LOGS = [];
      };
    }
    console.log(`[VisemeDebug:${debugLogName}] ${event} ${JSON.stringify(entry)}`);
  }, [debug, debugConsoleLog, debugLogName]);
  const minDisplayFrameMs = useMemo(
    () => getVisualProfileMinDisplayFrameMs(visualProfile),
    [visualProfile]
  );
  const visualDebugTimeScale = useMemo(
    () => debug && debugPlaybackRate > 0 && debugPlaybackRate < 1 ? 1 / debugPlaybackRate : 1,
    [debug, debugPlaybackRate]
  );
  const effectiveMinDisplayFrameMs = useMemo(
    () => Math.max(1, Math.round(minDisplayFrameMs * visualDebugTimeScale)),
    [minDisplayFrameMs, visualDebugTimeScale]
  );
  const generatedTransitionStepMs = useMemo(
    () => Math.max(1, Math.round(GENERATED_TRANSITION_FRAME_STEP_MS * visualDebugTimeScale)),
    [visualDebugTimeScale]
  );
  const transitionFrameProfileStepMs = useMemo(
    () => Math.max(1, Math.round(TRANSITION_FRAME_PROFILE_STEP_MS * visualDebugTimeScale)),
    [visualDebugTimeScale]
  );
  const legacyTransitionStepMs = useMemo(
    () => Math.max(1, Math.round(TRANSITION_DURATION_MS * visualDebugTimeScale)),
    [visualDebugTimeScale]
  );
  useEffect(() => {
    setDisplayTransitionMs(getVisualProfileCrossfadeMs(visualProfile));
  }, [visualProfile]);
  const closedFallback = mouthImageUrl || baseImageUrl;
  const speakingHalfBlinkUrl = runtimePackRoot ? `${runtimePackRoot}/eyes_half_blink.png` : null;
  const speakingClosedBlinkUrl = runtimePackRoot ? `${runtimePackRoot}/eyes_blink.png` : null;
  useEffect(() => {
    if (visualProfile !== "generatedTransitions" || !runtimePackRoot) {
      generatedTransitionImageUrlsRef.current = {};
      setGeneratedTransitionImageUrls({});
      setGeneratedTransitionPreloadComplete(false);
      return;
    }

    let cancelled = false;
    const candidates = buildGeneratedTransitionCandidates(runtimePackRoot);
    generatedTransitionImageUrlsRef.current = {};
    setGeneratedTransitionImageUrls({});
    setGeneratedTransitionPreloadComplete(false);

    Promise.all(
      candidates.map(async (candidate) => {
        const imageInfo = await preloadImageInfo(candidate.url);
        return imageInfo?.ok ? candidate : null;
      })
    ).then((loadedCandidates) => {
      if (cancelled) return;
      const loadedUrls = Object.fromEntries(
        loadedCandidates
          .filter((candidate): candidate is { key: string; url: string } => !!candidate)
          .map((candidate) => [candidate.key, candidate.url])
      );
      generatedTransitionImageUrlsRef.current = loadedUrls;
      setGeneratedTransitionImageUrls(loadedUrls);
      setGeneratedTransitionPreloadComplete(true);
      debugLog("transition-preload-complete", {
        loadedCount: Object.keys(loadedUrls).length,
        loadedKeys: Object.keys(loadedUrls),
        candidates: candidates.length,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [debugLog, runtimePackRoot, visualProfile]);
  const canvasImageUrls = useMemo(() => [
    baseImageUrl,
    closedFallback,
    ...Object.values(visemeImageUrls),
    ...Object.values(canonicalPoseImageUrls ?? {}),
    ...Object.values(generatedTransitionImageUrls),
    ...(speakingHalfBlinkUrl && speakingClosedBlinkUrl ? [speakingHalfBlinkUrl, speakingClosedBlinkUrl] : []),
    ...(eyeMotion?.canvasBlinkMaskUrl ? [eyeMotion.canvasBlinkMaskUrl] : []),
  ], [
    baseImageUrl,
    canonicalPoseImageUrls,
    closedFallback,
    eyeMotion?.canvasBlinkMaskUrl,
    generatedTransitionImageUrls,
    speakingClosedBlinkUrl,
    speakingHalfBlinkUrl,
    visemeImageUrls,
  ]);
  const canvasImageUrlsKey = useMemo(
    () => Array.from(new Set(canvasImageUrls)).sort().join("\n"),
    [canvasImageUrls]
  );
  const [loadedCanvasImageUrlsKey, setLoadedCanvasImageUrlsKey] = useState("");
  const canvasImagesReady = renderMode !== "canvas" || loadedCanvasImageUrlsKey === canvasImageUrlsKey;
  const handleImageLoadError = useCallback((imageKey: string | null) => {
    const canonicalPose = imageKey ? canonicalPoseFromDisplayKey(imageKey) : null;
    const failedUrl = canonicalPose ? canonicalPoseImageUrlsProp?.[canonicalPose] : null;
    if (!failedUrl) return;

    setFailedCanonicalImageUrls((current) => {
      if (current.has(failedUrl)) return current;
      const next = new Set(current);
      next.add(failedUrl);
      return next;
    });
  }, [canonicalPoseImageUrlsProp]);
  const resolveImageSrc = useCallback((imageKey: string | null): string | null => {
    if (!imageKey) return null;
    const generatedTransitionImageUrl = generatedTransitionImageUrlsRef.current[imageKey];
    if (generatedTransitionImageUrl) return generatedTransitionImageUrl;
    const canonicalPose = canonicalPoseFromDisplayKey(imageKey);
    if (canonicalPose) {
      return canonicalPoseImageUrls?.[canonicalPose] ?? closedFallback;
    }
    return imageKey === "closed" ? closedFallback : (visemeImageUrls[imageKey] || closedFallback);
  }, [canonicalPoseImageUrls, closedFallback, visemeImageUrls]);
  const renderCanvasFrame = useCallback(() => {
    if (renderMode !== "canvas") return;
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    const images = canvasImagesRef.current;
    const baseImage = images.get(baseImageUrl) ?? images.get(closedFallback);
    if (!canvas || !context || !baseImage) {
      if (debug && debugConsoleLog && now - canvasLastSkipLogAtRef.current >= CANVAS_FPS_LOG_INTERVAL_MS) {
        canvasLastSkipLogAtRef.current = now;
        debugLog("canvas-skip", {
          hasCanvas: !!canvas,
          hasContext: !!context,
          hasBaseImage: !!baseImage,
          imageCacheSize: images.size,
          baseImageUrl,
          closedFallback,
        });
      }
      return;
    }

    if (debug && debugConsoleLog) {
      if (canvasFpsWindowStartedAtRef.current === 0) {
        canvasFpsWindowStartedAtRef.current = now;
      }
      canvasRenderCountRef.current += 1;
      canvasTotalRenderCountRef.current += 1;
      canvasLastRenderDeltaMsRef.current = canvasLastRenderAtRef.current > 0
        ? now - canvasLastRenderAtRef.current
        : 0;
      canvasLastRenderAtRef.current = now;

      const fpsWindowMs = now - canvasFpsWindowStartedAtRef.current;
      if (fpsWindowMs >= CANVAS_FPS_LOG_INTERVAL_MS) {
        const fps = canvasRenderCountRef.current * 1000 / fpsWindowMs;
        canvasLastFpsRef.current = fps;
        debugLog("canvas-fps", {
          fps: Number(fps.toFixed(1)),
          renders: canvasRenderCountRef.current,
          totalRenders: canvasTotalRenderCountRef.current,
          windowMs: Math.round(fpsWindowMs),
          lastDeltaMs: Number(canvasLastRenderDeltaMsRef.current.toFixed(1)),
          displayFrame: parseGeneratedTransitionImageKey(displayImageRef.current),
          assetUrl: resolveImageSrc(displayImageRef.current),
          imageCacheSize: images.size,
        });
        canvasRenderCountRef.current = 0;
        canvasFpsWindowStartedAtRef.current = now;
      }
    }

    const width = canvasInternalWidth;
    const height = canvasInternalHeight;
    const mouthLayer = getOrCreateCanvas(canvasMouthLayerRef, width, height);
    const blinkLayer = getOrCreateCanvas(canvasBlinkLayerRef, width, height);
    context.clearRect(0, 0, width, height);
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    drawCoverImage(context, baseImage, width, height);

    const displaySrc = resolveImageSrc(displayImageRef.current);
    const displayImageElement = displaySrc ? images.get(displaySrc) : null;
    if (debug && debugConsoleLog && displaySrc && !displayImageElement && !loggedMissingCanvasImagesRef.current.has(displaySrc)) {
      loggedMissingCanvasImagesRef.current.add(displaySrc);
      debugLog("canvas-missing-display-image", {
        displayImage: displayImageRef.current,
        displayFrame: parseGeneratedTransitionImageKey(displayImageRef.current),
        displaySrc,
        imageCacheSize: images.size,
      });
    }
    if (displayImageElement) {
      drawFeatheredMouthPatch(
        context,
        mouthLayer,
        displayImageElement,
        width,
        height,
        jawOpenValueRef.current,
        jawMotionConfig
      );
    }

    const phase = blinkPhaseRef.current;
    const halfBlinkOpacity =
      phase === "half_closing" || phase === "half_opening"
        ? (eyeMotion?.opacity ?? 1)
        : phase === "closed"
        ? (eyeMotion?.opacity ?? 1) * 0.28
        : 0;
    const closedBlinkOpacity = phase === "closed" ? (eyeMotion?.opacity ?? 1) : 0;
    const halfBlinkImage = speakingHalfBlinkUrl ? images.get(speakingHalfBlinkUrl) : null;
    const closedBlinkImage = speakingClosedBlinkUrl ? images.get(speakingClosedBlinkUrl) : null;
    const blinkMaskImage = eyeMotion?.canvasBlinkMaskUrl ? images.get(eyeMotion.canvasBlinkMaskUrl) : null;
    if (halfBlinkImage) {
      drawCanvasBlinkOverlay(context, blinkLayer, halfBlinkImage, width, height, eyeMotion, canvasEyePosition, halfBlinkOpacity, blinkMaskImage);
    }
    if (closedBlinkImage) {
      drawCanvasBlinkOverlay(context, blinkLayer, closedBlinkImage, width, height, eyeMotion, canvasEyePosition, closedBlinkOpacity, blinkMaskImage);
    }
  }, [
    baseImageUrl,
    canvasEyePosition,
    canvasInternalHeight,
    canvasInternalWidth,
    closedFallback,
    debug,
    debugConsoleLog,
    debugLog,
    eyeMotion,
    jawMotionConfig,
    renderMode,
    resolveImageSrc,
    speakingClosedBlinkUrl,
    speakingHalfBlinkUrl,
  ]);
  const updateDebugSnapshot = useCallback((snapshot: VisemeDebugSnapshot) => {
    if (!debug) return;
    const key = JSON.stringify(snapshot);
    if (key !== lastDebugKeyRef.current) {
      lastDebugKeyRef.current = key;
      setDebugSnapshot(snapshot);
    }
  }, [debug]);

  const updatePlaybackClock = useCallback((
    timeMs: number,
    durationMs: number | null,
    status: VisemePlaybackStatus
  ) => {
    if (!playbackClockRef) return;
    playbackClockRef.current = {
      timeMs,
      durationMs,
      status,
      playbackRate: debugPlaybackRate,
    };
  }, [debugPlaybackRate, playbackClockRef]);

  useEffect(() => {
    if (renderMode !== "canvas") return;

    let cancelled = false;
    const imageUrlsKey = canvasImageUrlsKey;
    Promise.all(
      Array.from(new Set(canvasImageUrls)).map(async (url) => {
        try {
          return [url, await loadCanvasImage(url)] as const;
        } catch {
          return null;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      canvasImagesRef.current = new Map(entries.filter((entry): entry is readonly [string, HTMLImageElement] => !!entry));
      setLoadedCanvasImageUrlsKey(imageUrlsKey);
      debugLog("canvas-preload-complete", {
        requested: Array.from(new Set(canvasImageUrls)).length,
        loaded: canvasImagesRef.current.size,
        missing: Array.from(new Set(canvasImageUrls)).filter((url) => !canvasImagesRef.current.has(url)),
      });
      renderCanvasFrame();
    }).catch((error) => {
      console.error("[VisemePlayer] canvas image preload failed", error);
    });

    return () => {
      cancelled = true;
    };
  }, [canvasImageUrls, canvasImageUrlsKey, debugLog, renderCanvasFrame, renderMode]);

  const applyJawOpenValue = useCallback((jawOpen: number) => {
    jawOpenValueRef.current = jawOpen;
    const jawLayer = jawLayerRef.current;
    if (!jawLayer) return;

    const jawTranslateY = jawMotionConfig.maxTranslateYPercent * jawOpen;
    const jawScaleY = 1 + jawMotionConfig.maxScaleYDelta * jawOpen;
    const jawScaleX = 1 + jawMotionConfig.maxScaleXDelta * jawOpen;
    jawLayer.style.transform = `translateY(${jawTranslateY}%) scaleX(${jawScaleX}) scaleY(${jawScaleY})`;
  }, [jawMotionConfig]);

  const applyBlinkPhase = useCallback((phase: BlinkPhase) => {
    blinkPhaseRef.current = phase;
    const baseOpacity = eyeMotion?.opacity ?? 1;
    const halfBlinkOpacity =
      phase === "half_closing" || phase === "half_opening"
        ? baseOpacity
        : phase === "closed"
        ? baseOpacity * 0.28
        : 0;
    const closedBlinkOpacity = phase === "closed" ? baseOpacity : 0;

    if (halfBlinkRef.current) {
      halfBlinkRef.current.style.opacity = String(halfBlinkOpacity);
    }
    if (closedBlinkRef.current) {
      closedBlinkRef.current.style.opacity = String(closedBlinkOpacity);
    }
    renderCanvasFrame();
  }, [eyeMotion?.opacity, renderCanvasFrame]);

  const doSpeakingBlink = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const sequence = ++blinkSequenceRef.current;
      const isActive = () => blinkSequenceRef.current === sequence;

      applyBlinkPhase("half_closing");
      window.setTimeout(() => {
        if (!isActive()) {
          resolve();
          return;
        }
        applyBlinkPhase("closed");
        window.setTimeout(() => {
          if (!isActive()) {
            resolve();
            return;
          }
          applyBlinkPhase("half_opening");
          window.setTimeout(() => {
            if (!isActive()) {
              resolve();
              return;
            }
            applyBlinkPhase("open");
            resolve();
          }, SPEAKING_BLINK_TIMING.half_opening);
        }, SPEAKING_BLINK_TIMING.closed);
      }, SPEAKING_BLINK_TIMING.half_closing);
    });
  }, [applyBlinkPhase]);

  const getTrackedVisemeWindow = useCallback((timeMs: number): VisemeWindow | null => {
    const events = normalizedVisemes;
    if (!events.length) return null;

    let currentIdx = Math.max(
      0,
      Math.min(visemeWindowIndexRef.current, events.length - 1)
    );

    if (events[currentIdx].timeMs > timeMs) {
      while (currentIdx > 0 && events[currentIdx].timeMs > timeMs) {
        currentIdx -= 1;
      }
    } else {
      while (currentIdx + 1 < events.length && events[currentIdx + 1].timeMs <= timeMs) {
        currentIdx += 1;
      }
    }

    visemeWindowIndexRef.current = currentIdx;

    if (events[currentIdx].timeMs > timeMs) {
      return { prev: events[0], current: events[0], next: events[0] };
    }

    return {
      prev: events[Math.max(0, currentIdx - 1)],
      current: events[currentIdx],
      next: currentIdx + 1 < events.length ? events[currentIdx + 1] : null,
    };
  }, [normalizedVisemes]);

  const commitDisplayImage = useCallback((nextImage: string) => {
    if (!nextImage || nextImage === displayImageRef.current) {
      return;
    }

    const previousImage = displayImageRef.current;
    const transitionMs = getTransitionCrossfadeMs(previousImage, nextImage, visualProfile);
    window.clearTimeout(crossfadeTimeoutRef.current);
    cancelAnimationFrame(crossfadeFrameRef.current);
    setDisplayTransitionMs(transitionMs);

    if (shouldHardCutTransition(previousImage, nextImage, visualProfile)) {
      lastTransitionModeRef.current = "hardCut";
      debugLog("display-commit", {
        mode: "hardCut",
        from: previousImage,
        to: nextImage,
        frame: parseGeneratedTransitionImageKey(nextImage),
        assetSource: assetSourceForDisplayKey(nextImage, visemeImageUrls, canonicalPoseImageUrls),
        assetUrl: resolveImageSrc(nextImage),
        transitionMs,
        elapsedSincePrevMs: Number((performance.now() - lastDisplayChangeAtRef.current).toFixed(1)),
      });
      setFadeFromImage(null);
      setDisplayOpacity(1);
      setDisplayImage(nextImage);
      displayImageRef.current = nextImage;
      lastDisplayChangeAtRef.current = performance.now();
      renderCanvasFrame();
      return;
    }

    lastTransitionModeRef.current = "alpha";
    debugLog("display-commit", {
      mode: "alpha",
      from: previousImage,
      to: nextImage,
      frame: parseGeneratedTransitionImageKey(nextImage),
      assetSource: assetSourceForDisplayKey(nextImage, visemeImageUrls, canonicalPoseImageUrls),
      assetUrl: resolveImageSrc(nextImage),
      transitionMs,
      elapsedSincePrevMs: Number((performance.now() - lastDisplayChangeAtRef.current).toFixed(1)),
    });
    setFadeFromImage(previousImage);
    setDisplayOpacity(0);
    setDisplayImage(nextImage);
    displayImageRef.current = nextImage;
    lastDisplayChangeAtRef.current = performance.now();
    renderCanvasFrame();

    crossfadeFrameRef.current = requestAnimationFrame(() => {
      setDisplayOpacity(1);
    });

    crossfadeTimeoutRef.current = window.setTimeout(() => {
      setFadeFromImage(null);
    }, transitionMs + 18);
  }, [
    canonicalPoseImageUrls,
    debugLog,
    renderCanvasFrame,
    resolveImageSrc,
    visemeImageUrls,
    visualProfile,
  ]);

  const processDisplayQueue = useCallback(() => {
    window.clearTimeout(minDisplayTimerRef.current);
    minDisplayTimerRef.current = 0;

    const nextImage = displayQueueRef.current[0];
    if (!nextImage) return;

    const now = performance.now();
    const elapsedMs = now - lastDisplayChangeAtRef.current;
    const waitMs = effectiveMinDisplayFrameMs - elapsedMs;
    if (waitMs > 0) {
      minDisplayTimerRef.current = window.setTimeout(processDisplayQueue, waitMs);
      return;
    }

    displayQueueRef.current.shift();
    debugLog("queue-commit", {
      nextImage,
      frame: parseGeneratedTransitionImageKey(nextImage),
      elapsedSincePrevMs: Number(elapsedMs.toFixed(1)),
      remainingQueue: displayQueueRef.current,
    });
    if (displayQueueRef.current.length === 0) {
      displayQueueFirstQueuedAtRef.current = 0;
    } else {
      displayQueueFirstQueuedAtRef.current = performance.now();
    }
    commitDisplayImage(nextImage);
    if (displayQueueRef.current.length > 0) {
      minDisplayTimerRef.current = window.setTimeout(processDisplayQueue, effectiveMinDisplayFrameMs);
    }
  }, [commitDisplayImage, debugLog, effectiveMinDisplayFrameMs]);

  const queueDisplayImage = useCallback((nextImage: string) => {
    if (!nextImage || nextImage === displayImageRef.current) {
      return;
    }

    const queue = displayQueueRef.current;
    const queueBefore = [...queue];
    if (queue[queue.length - 1] === nextImage) {
      return;
    }

    // Audio visemes can arrive faster than the minimum visual hold. G drops stale
    // pending frames; H preserves one open vowel so it is not skipped entirely.
    const now = performance.now();
    const pendingImage = queue[0];
    const pendingAgeMs = displayQueueFirstQueuedAtRef.current > 0
      ? now - displayQueueFirstQueuedAtRef.current
      : 0;
    const shouldPreservePendingVowel =
      (visualProfile === "priorityVowelHold" || isEndingSettleProfile(visualProfile)) &&
      !!pendingImage &&
      pendingImage !== nextImage &&
      isPriorityVowelDisplayKey(pendingImage) &&
      pendingAgeMs <= PRIORITY_VOWEL_PENDING_MAX_MS;

    displayQueueRef.current = shouldPreservePendingVowel
      ? [pendingImage, nextImage]
      : [nextImage];
    displayQueueFirstQueuedAtRef.current = shouldPreservePendingVowel
      ? (displayQueueFirstQueuedAtRef.current || now)
      : now;
    debugLog("queue-set", {
      nextImage,
      frame: parseGeneratedTransitionImageKey(nextImage),
      queueBefore,
      queueAfter: displayQueueRef.current,
      preservePendingVowel: shouldPreservePendingVowel,
      pendingAgeMs: Number(pendingAgeMs.toFixed(1)),
    });
    processDisplayQueue();
  }, [debugLog, processDisplayQueue, visualProfile]);

  const scheduleDisplayImages = useCallback((
    transitionFrames: string[],
    finalImage: string,
    stepMs: number
  ) => {
    const sequence = [...transitionFrames, finalImage].filter(Boolean);
    if (sequence.length === 0) return;

    window.clearTimeout(transitionTimeoutRef.current);
    activeTransitionTargetRef.current = finalImage;
    debugLog("sequence-schedule", {
      transitionFrames,
      finalImage,
      sequence,
      stepMs,
    });
    queueDisplayImage(sequence[0]);

    const scheduleNext = (index: number) => {
      if (index >= sequence.length) {
        activeTransitionTargetRef.current = null;
        return;
      }
      transitionTimeoutRef.current = window.setTimeout(() => {
        queueDisplayImage(sequence[index]);
        scheduleNext(index + 1);
      }, stepMs);
    };

    scheduleNext(1);
  }, [debugLog, queueDisplayImage]);

  const updateVisualAtTime = useCallback((
    timeMs: number,
    durationMs: number | null,
    status: VisemePlaybackStatus
  ) => {
    lastVisualTimeMsRef.current = timeMs;
    lastPlaybackStatusRef.current = status;
    const visemeWindow = getTrackedVisemeWindow(timeMs);
    const holdCurrentVowel =
      isVowelHoldProfile(visualProfile) &&
      shouldHoldCurrentVowel(visemeWindow, timeMs, canonicalPoseImageUrls);
    const visualEvent =
      (visualProfile === "anticipatoryBlend" ||
        visualProfile === "slowCloseReturn" ||
        (isVowelHoldProfile(visualProfile) && !holdCurrentVowel)) &&
      visemeWindow?.next &&
      shouldAnticipateNextViseme(visemeWindow, timeMs)
        ? visemeWindow.next
        : visemeWindow?.current;
    const baseViseme = visualEvent
      ? displayVisemeName(visualEvent, canonicalPoseImageUrls)
      : "closed";
    if (debug && debugConsoleLog && baseViseme !== lastLoggedTargetRef.current) {
      lastLoggedTargetRef.current = baseViseme;
      debugLog("target-change", {
        targetImage: baseViseme,
        targetFrame: parseGeneratedTransitionImageKey(baseViseme),
        assetSource: assetSourceForDisplayKey(baseViseme, visemeImageUrls, canonicalPoseImageUrls),
        assetUrl: resolveImageSrc(baseViseme),
        azureVisemeId: visualEvent?.azureVisemeId ?? visemeWindow?.current.azureVisemeId,
        dentalPose: visualEvent?.dentalPose ?? visemeWindow?.current.dentalPose,
        legacyViseme: visualEvent?.viseme ?? visemeWindow?.current.viseme,
        currentEventTimeMs: visemeWindow?.current.timeMs,
        nextEventTimeMs: visemeWindow?.next?.timeMs,
        holdMs: visemeWindow?.next
          ? visemeWindow.next.timeMs - visemeWindow.current.timeMs
          : null,
      });
    }
    const jawTarget = getJawOpenForWindow(visemeWindow, timeMs, durationMs, canonicalPoseImageUrls);
    const prevJawOpen = jawOpenValueRef.current;
    const delta = jawTarget - prevJawOpen;
    const remainingPlaybackMs =
      durationMs && Number.isFinite(durationMs) ? durationMs - timeMs : null;
    const isFinalJawRelease =
      remainingPlaybackMs !== null && remainingPlaybackMs <= JAW_FINAL_RELEASE_MS;
    const jawSmoothing = isFinalJawRelease
      ? 1
      : delta < 0
      ? (
        isSlowCloseProfile(visualProfile)
          ? 0.16
          : (jawTarget === 0 ? 0.30 : 0.12)
      )
      : 0.16;
    const nextJawOpen = Math.abs(delta) < 0.001
      ? jawTarget
      : prevJawOpen + delta * jawSmoothing;
    applyJawOpenValue(nextJawOpen);
    const sustainedHoldFrame = visemeWindow
      ? getSustainedHoldFrame(baseViseme, visemeWindow.current, visemeWindow.next, timeMs)
      : null;
    const finalReleaseFrame = getFinalReleaseFrame(baseViseme, durationMs, timeMs, visualProfile);
    const overrideFrame = finalReleaseFrame ?? sustainedHoldFrame;
    const shouldDeferShortTarget =
      visualProfile === "partialBlend" &&
      !overrideFrame &&
      shouldDeferShortVisualTarget(visemeWindow, baseViseme, displayImageRef.current, timeMs);

    if (shouldDeferShortTarget) {
      window.clearTimeout(transitionTimeoutRef.current);
      activeTransitionTargetRef.current = null;
      lastTransitionModeRef.current = "hold";
    }

    if (!shouldDeferShortTarget) {
      if (overrideFrame && overrideFrame !== displayImageRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        activeTransitionTargetRef.current = null;
        queueDisplayImage(overrideFrame);
      } else if (!overrideFrame && baseViseme !== prevBaseVisemeRef.current) {
        const transitionFrames = getTransitionFrames(
          prevBaseVisemeRef.current,
          baseViseme,
          visemeWindow,
          visualProfile,
          visemeImageUrls,
          generatedTransitionImageUrlsRef.current
        );

        if (transitionFrames.length > 0) {
          const transitionStepMs =
            visualProfile === "generatedTransitions"
              ? generatedTransitionStepMs
              : visualProfile === "transitionFrames"
              ? transitionFrameProfileStepMs
              : legacyTransitionStepMs;
          debugLog("transition-frames", {
            from: prevBaseVisemeRef.current,
            to: baseViseme,
            transitionFrames,
            finalImage: baseViseme,
            stepMs: transitionStepMs,
            generatedPair: generatedTransitionPairKey(prevBaseVisemeRef.current, baseViseme),
            generatedPlan: getGeneratedTransitionFramePlan(prevBaseVisemeRef.current, baseViseme),
            holdMs: visemeWindow?.next
              ? visemeWindow.next.timeMs - visemeWindow.current.timeMs
              : null,
          });
          scheduleDisplayImages(
            transitionFrames,
            baseViseme,
            transitionStepMs
          );
        } else {
          debugLog("transition-fallback", {
            from: prevBaseVisemeRef.current,
            to: baseViseme,
            generatedPair: generatedTransitionPairKey(prevBaseVisemeRef.current, baseViseme),
            generatedPlan: getGeneratedTransitionFramePlan(prevBaseVisemeRef.current, baseViseme),
            generatedTransitionLoadedCount: Object.keys(generatedTransitionImageUrlsRef.current).length,
            holdMs: visemeWindow?.next
              ? visemeWindow.next.timeMs - visemeWindow.current.timeMs
              : null,
          });
          window.clearTimeout(transitionTimeoutRef.current);
          activeTransitionTargetRef.current = null;
          queueDisplayImage(baseViseme);
        }
      } else if (
        !overrideFrame &&
        baseViseme !== displayImageRef.current &&
        activeTransitionTargetRef.current !== baseViseme
      ) {
        queueDisplayImage(baseViseme);
      }
    }

    prevBaseVisemeRef.current = baseViseme;
    if (debug && visemeWindow?.current) {
      const nowMs = typeof performance !== "undefined" ? performance.now() : Date.now();
      const activeDisplayImage = displayImageRef.current;
      const targetAssetSource = assetSourceForDisplayKey(baseViseme, visemeImageUrls, canonicalPoseImageUrls);
      const shouldUpdateDebug =
        nowMs - lastDebugUpdateAtRef.current >= DEBUG_SNAPSHOT_INTERVAL_MS ||
        activeDisplayImage !== lastDebugDisplayRef.current ||
        baseViseme !== lastDebugTargetRef.current;
      if (shouldUpdateDebug) {
        lastDebugUpdateAtRef.current = nowMs;
        lastDebugDisplayRef.current = activeDisplayImage;
        lastDebugTargetRef.current = baseViseme;
        updateDebugSnapshot({
          timeMs: Math.round(timeMs),
          azureVisemeId: visualEvent?.azureVisemeId ?? visemeWindow.current.azureVisemeId,
          dentalPose: visualEvent?.dentalPose ?? visemeWindow.current.dentalPose,
          legacyViseme: visualEvent?.viseme ?? visemeWindow.current.viseme,
          targetImage: baseViseme,
          targetAssetSource,
          displayImage: activeDisplayImage,
          assetSource: assetSourceForDisplayKey(activeDisplayImage, visemeImageUrls, canonicalPoseImageUrls),
          assetUrl: resolveImageSrc(activeDisplayImage) ?? "",
          runtimePackRoot,
          transitionMode: lastTransitionModeRef.current,
          jawOpen: Number(nextJawOpen.toFixed(3)),
          jawTranslateY: Number((jawMotionConfig.maxTranslateYPercent * nextJawOpen).toFixed(3)),
          jawScaleY: Number((1 + jawMotionConfig.maxScaleYDelta * nextJawOpen).toFixed(4)),
          playbackRate: debugPlaybackRate,
          status,
          transitionLoadedCount: Object.keys(generatedTransitionImageUrlsRef.current).length,
          transitionPreloadComplete: generatedTransitionPreloadComplete,
          canvasImagesReady,
          displayQueueLength: displayQueueRef.current.length,
          activeTransitionTarget: activeTransitionTargetRef.current,
          minDisplayFrameMs: effectiveMinDisplayFrameMs,
          canvasFps: Number(canvasLastFpsRef.current.toFixed(1)),
          canvasRenderCount: canvasTotalRenderCountRef.current,
          canvasRenderDeltaMs: Number(canvasLastRenderDeltaMsRef.current.toFixed(1)),
        });
      }
    }
    renderCanvasFrame();
  }, [
    applyJawOpenValue,
    canonicalPoseImageUrls,
    canvasImagesReady,
    debug,
    debugConsoleLog,
    debugLog,
    debugPlaybackRate,
    effectiveMinDisplayFrameMs,
    generatedTransitionStepMs,
    generatedTransitionPreloadComplete,
    getTrackedVisemeWindow,
    jawMotionConfig.maxScaleYDelta,
    jawMotionConfig.maxTranslateYPercent,
    legacyTransitionStepMs,
    queueDisplayImage,
    resolveImageSrc,
    runtimePackRoot,
    scheduleDisplayImages,
    transitionFrameProfileStepMs,
    renderCanvasFrame,
    updateDebugSnapshot,
    visemeImageUrls,
    visualProfile,
  ]);

  const resetVisualState = useCallback((status: VisemePlaybackStatus = "ended") => {
    cancelAnimationFrame(animFrameRef.current);
    window.clearTimeout(playbackStartTimerRef.current);
    window.clearTimeout(transitionTimeoutRef.current);
    window.clearTimeout(crossfadeTimeoutRef.current);
    window.clearTimeout(minDisplayTimerRef.current);
    cancelAnimationFrame(crossfadeFrameRef.current);
    activeTransitionTargetRef.current = null;
    displayQueueRef.current = [];
    displayQueueFirstQueuedAtRef.current = 0;
    const resetNow = typeof performance !== "undefined" ? performance.now() : Date.now();
    const fpsWindowMs = canvasFpsWindowStartedAtRef.current > 0
      ? resetNow - canvasFpsWindowStartedAtRef.current
      : 0;
    const summaryFps = fpsWindowMs > 0
      ? canvasRenderCountRef.current * 1000 / fpsWindowMs
      : canvasLastFpsRef.current;
    debugLog("canvas-summary", {
      status,
      fps: Number(summaryFps.toFixed(1)),
      rendersInWindow: canvasRenderCountRef.current,
      totalRenders: canvasTotalRenderCountRef.current,
      windowMs: Math.round(fpsWindowMs),
      lastDeltaMs: Number(canvasLastRenderDeltaMsRef.current.toFixed(1)),
      lastDisplay: displayImageRef.current,
      lastFrame: parseGeneratedTransitionImageKey(displayImageRef.current),
    });
    lastLoggedTargetRef.current = "";
    lastPlaybackStatusRef.current = status;
    canvasRenderCountRef.current = 0;
    canvasFpsWindowStartedAtRef.current = 0;
    canvasLastRenderAtRef.current = 0;
    canvasLastFpsRef.current = 0;
    canvasLastRenderDeltaMsRef.current = 0;
    canvasTotalRenderCountRef.current = 0;
    loggedMissingCanvasImagesRef.current.clear();
    setDisplayImage("closed");
    displayImageRef.current = "closed";
    lastDisplayChangeAtRef.current = performance.now();
    lastTransitionModeRef.current = "hold";
    if (debug) {
      setDebugSnapshot((current) => current ? { ...current, status } : current);
    } else {
      setDebugSnapshot(null);
    }
    setFadeFromImage(null);
    setDisplayOpacity(1);
    applyJawOpenValue(0);
    prevBaseVisemeRef.current = "closed";
    debugLog("reset", { status });
    renderCanvasFrame();
  }, [applyJawOpenValue, debug, debugLog, renderCanvasFrame]);

  useEffect(() => {
    if (
      visualProfile === "generatedTransitions" &&
      runtimePackRoot &&
      !generatedTransitionPreloadComplete
    ) {
      return;
    }
    if (!canvasImagesReady) {
      return;
    }

    visemeWindowIndexRef.current = 0;
    const preloadUrls = [
      baseImageUrl,
      closedFallback,
      ...Object.values(visemeImageUrls),
      ...Object.values(canonicalPoseImageUrls ?? {}),
      ...(speakingHalfBlinkUrl && speakingClosedBlinkUrl ? [speakingHalfBlinkUrl, speakingClosedBlinkUrl] : []),
    ];

    const initializeVisualState = (timeMs: number, playInitialTransition = true) => {
      cancelAnimationFrame(animFrameRef.current);
      visemeWindowIndexRef.current = 0;
      const initialWindow = getTrackedVisemeWindow(timeMs);
      const initialViseme = initialWindow
        ? displayVisemeName(initialWindow.current, canonicalPoseImageUrls)
        : "closed";
      const initialTransitionFrames =
        visualProfile === "generatedTransitions" && initialViseme !== "closed"
          ? getTransitionFrames(
            "closed",
            initialViseme,
            initialWindow,
            visualProfile,
            visemeImageUrls,
            generatedTransitionImageUrlsRef.current
          )
          : [];
      const shouldPlayInitialGeneratedTransition = playInitialTransition && initialTransitionFrames.length > 0;
      const initialDisplayImage = shouldPlayInitialGeneratedTransition ? "closed" : initialViseme;

      debugLog("initialize", {
        timeMs: Math.round(timeMs),
        playInitialTransition,
        initialViseme,
        initialDisplayImage,
        initialTransitionFrames,
        shouldPlayInitialGeneratedTransition,
        transitionLoadedCount: Object.keys(generatedTransitionImageUrlsRef.current).length,
        canvasImagesReady,
      });
      setFadeFromImage(null);
      setDisplayOpacity(1);
      displayQueueRef.current = [];
      displayQueueFirstQueuedAtRef.current = 0;
      window.clearTimeout(minDisplayTimerRef.current);
      window.clearTimeout(transitionTimeoutRef.current);
      activeTransitionTargetRef.current = null;
      setDisplayImage(initialDisplayImage);
      displayImageRef.current = initialDisplayImage;
      lastDisplayChangeAtRef.current = performance.now();
      lastTransitionModeRef.current = "hold";
      prevBaseVisemeRef.current = initialViseme;
      applyJawOpenValue(
        shouldPlayInitialGeneratedTransition
          ? 0
          : getJawOpenForWindow(initialWindow, timeMs, null, canonicalPoseImageUrls)
      );
      renderCanvasFrame();

      if (shouldPlayInitialGeneratedTransition) {
        scheduleDisplayImages(
          initialTransitionFrames,
          initialViseme,
          generatedTransitionStepMs
        );
      }
    };

    if (playbackMode === "clock") {
      audioRef.current = null;
      let didResetAfterEnd = false;
      let didInitializePlaying = false;
      let cancelled = false;

      const tickClock = () => {
        if (cancelled) return;
        const clock = playbackClockRef?.current;

        if (clock?.status === "playing") {
          didResetAfterEnd = false;
          const timeMs = Math.max(0, clock.timeMs + visualLeadMs);
          if (!didInitializePlaying) {
            didInitializePlaying = true;
            initializeVisualState(timeMs, true);
          }
          updateVisualAtTime(timeMs, clock.durationMs, "playing");
        } else if (clock?.status === "ended") {
          if (!didResetAfterEnd) {
            didResetAfterEnd = true;
            didInitializePlaying = false;
            resetVisualState("ended");
          }
        }

        animFrameRef.current = requestAnimationFrame(tickClock);
      };

      void preloadImageUrls(preloadUrls).then(() => {
        if (cancelled || playbackMode !== "clock") return;
        initializeVisualState(visualLeadMs, false);
        animFrameRef.current = requestAnimationFrame(tickClock);
      });

      return () => {
        cancelled = true;
        audioRef.current = null;
        resetVisualState("ended");
        window.clearTimeout(blinkTimerRef.current);
        blinkSequenceRef.current += 1;
        applyBlinkPhase("open");
      };
    }

    const audio = reusableAudioRef.current ?? new Audio();
    reusableAudioRef.current = audio;
    setPlaybackIssue(null);
    audioRef.current = audio;
    audio.pause();
    audio.src = audioUrl;
    audio.muted = audioMuted;
    audio.volume = audioMuted ? 0 : 0.9;
    audio.defaultPlaybackRate = debugPlaybackRate;
    audio.playbackRate = debugPlaybackRate;
    audio.preload = "auto";
    audio.load();

    const updateViseme = () => {
      if (audio && !audio.paused && !audio.ended) {
        const timeMs = Math.max(0, audio.currentTime * 1000 + visualLeadMs);
        const durationMs =
          Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : null;
        updatePlaybackClock(Math.max(0, audio.currentTime * 1000), durationMs, "playing");
        updateVisualAtTime(timeMs, durationMs, "playing");
        animFrameRef.current = requestAnimationFrame(updateViseme);
      }
    };

    const startAnimation = () => {
      window.clearTimeout(playbackStartWatchdogRef.current);
      setPlaybackIssue(null);
      const initialTimeMs = Math.max(0, audio.currentTime * 1000 + visualLeadMs);
      const durationMs =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : null;
      updatePlaybackClock(Math.max(0, audio.currentTime * 1000), durationMs, "playing");
      initializeVisualState(initialTimeMs, true);
      animFrameRef.current = requestAnimationFrame(updateViseme);
    };

    const handlePause = () => {
      cancelAnimationFrame(animFrameRef.current);
      const durationMs =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : null;
      updatePlaybackClock(Math.max(0, audio.currentTime * 1000), durationMs, "paused");
    };

    const handleEnded = () => {
      window.clearTimeout(playbackStartWatchdogRef.current);
      resetVisualState("ended");
      const durationMs =
        Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : null;
      updatePlaybackClock(durationMs ?? Math.max(0, audio.currentTime * 1000), durationMs, "ended");
      onEndedRef.current?.();
    };

    const handleError = (e: Event) => {
      console.error("[VisemePlayer] Audio error:", e);
      window.clearTimeout(playbackStartWatchdogRef.current);
      updatePlaybackClock(Math.max(0, audio.currentTime * 1000), null, "paused");
      reportAudioPlaybackIssue("viseme-audio-error", e);
    };

    audio.addEventListener("playing", startAnimation);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    const preloadAndPlay = async () => {
      let preloadTimeout = 0;
      try {
        await Promise.race([
          Promise.all([
            waitForAudioCanPlay(audio),
            preloadImageUrls(preloadUrls),
          ]),
          new Promise<never>((_, reject) => {
            preloadTimeout = window.setTimeout(() => {
              reject(new Error("viseme audio preload timeout"));
            }, 12000);
          }),
        ]);
      } catch (error) {
        if (audioRef.current === audio) {
          reportAudioPlaybackIssue("viseme-audio-preload-timeout", error);
        }
        return;
      } finally {
        window.clearTimeout(preloadTimeout);
      }

      if (audioRef.current !== audio) return;

      visemeWindowIndexRef.current = 0;
      initializeVisualState(visualLeadMs, false);
      updatePlaybackClock(0, Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration * 1000 : null, "idle");

      playbackStartTimerRef.current = window.setTimeout(() => {
        if (audioRef.current !== audio) return;
        audio.defaultPlaybackRate = debugPlaybackRate;
        audio.playbackRate = debugPlaybackRate;
        audio.play().then(() => {
          markMediaPlaybackUnlocked();
        }).catch((e) => {
          console.error("[VisemePlayer] Auto-play failed:", e);
          reportAudioPlaybackIssue("viseme-audio-play-failed", e);
        });
      }, initialPlaybackDelayMs);

      playbackStartWatchdogRef.current = window.setTimeout(() => {
        if (audioRef.current !== audio) return;
        if (!hasMediaPlaybackStarted(audio)) {
          reportAudioPlaybackIssue("viseme-audio-start-timeout");
        }
      }, initialPlaybackDelayMs + 12000);
    };

    preloadAndPlay();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.clearTimeout(playbackStartTimerRef.current);
      window.clearTimeout(playbackStartWatchdogRef.current);
      window.clearTimeout(transitionTimeoutRef.current);
      window.clearTimeout(crossfadeTimeoutRef.current);
      window.clearTimeout(minDisplayTimerRef.current);
      cancelAnimationFrame(crossfadeFrameRef.current);
      activeTransitionTargetRef.current = null;
      displayQueueRef.current = [];
      displayQueueFirstQueuedAtRef.current = 0;
      window.clearTimeout(blinkTimerRef.current);
      blinkSequenceRef.current += 1;
      applyBlinkPhase("open");
      audio.removeEventListener("playing", startAnimation);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };
  }, [
    audioUrl,
    audioMuted,
    baseImageUrl,
    canonicalPoseImageUrls,
    canvasImagesReady,
    closedFallback,
    debug,
    debugLog,
    debugPlaybackRate,
    generatedTransitionStepMs,
    generatedTransitionPreloadComplete,
    getTrackedVisemeWindow,
    initialPlaybackDelayMs,
    normalizedVisemes,
    applyJawOpenValue,
    applyBlinkPhase,
    playbackClockRef,
    playbackMode,
    reportAudioPlaybackIssue,
    renderCanvasFrame,
    resetVisualState,
    runtimePackRoot,
    scheduleDisplayImages,
    speakingClosedBlinkUrl,
    speakingHalfBlinkUrl,
    updatePlaybackClock,
    updateVisualAtTime,
    visualLeadMs,
    visemeImageUrls,
    visualProfile,
  ]);

  useEffect(() => {
    if (!eyeMotion || !speakingHalfBlinkUrl || !speakingClosedBlinkUrl) {
      window.clearTimeout(blinkTimerRef.current);
      blinkSequenceRef.current += 1;
      applyBlinkPhase("open");
      return;
    }

    let cancelled = false;
    const intervalMinMs = eyeMotion.intervalMinMs ?? 1300;
    const intervalMaxMs = Math.max(intervalMinMs, eyeMotion.intervalMaxMs ?? 3200);
    const firstBlinkMinMs = eyeMotion.firstBlinkMinMs ?? Math.min(1100, intervalMinMs);
    const firstBlinkMaxMs = Math.max(firstBlinkMinMs, eyeMotion.firstBlinkMaxMs ?? Math.min(1900, intervalMaxMs));
    const firstBlinkDelayMs = firstBlinkMinMs + Math.random() * (firstBlinkMaxMs - firstBlinkMinMs);

    const getRemainingPlaybackWallMs = (): number | null => {
      if (playbackMode === "clock") {
        const clock = playbackClockRef?.current;
        if (!clock || !Number.isFinite(clock.durationMs ?? NaN) || !clock.durationMs) return null;
        const playbackRate = clock.playbackRate > 0 ? clock.playbackRate : debugPlaybackRate;
        return Math.max(0, (clock.durationMs - clock.timeMs) / playbackRate);
      }

      const audio = audioRef.current;
      if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return null;
      const playbackRate = audio.playbackRate > 0 ? audio.playbackRate : debugPlaybackRate;
      return Math.max(0, ((audio.duration - audio.currentTime) * 1000) / playbackRate);
    };

    const scheduleBlink = (delayMs?: number) => {
      const delay = delayMs ?? (intervalMinMs + Math.random() * (intervalMaxMs - intervalMinMs));
      blinkTimerRef.current = window.setTimeout(() => {
        if (cancelled) return;

        const audio = audioRef.current;
        const isPlaybackActive =
          playbackMode === "clock"
            ? playbackClockRef?.current.status === "playing"
            : !!audio && !audio.paused && !audio.ended;
        if (!isPlaybackActive || Date.now() < blinkResumeAtRef.current) {
          scheduleBlink(220);
          return;
        }

        const remainingPlaybackMs = getRemainingPlaybackWallMs();
        if (remainingPlaybackMs !== null && remainingPlaybackMs <= SPEAKING_BLINK_END_GUARD_MS) {
          scheduleBlink(220);
          return;
        }

        blinkResumeAtRef.current = Date.now() + 260;
        doSpeakingBlink().then(() => {
          if (!cancelled) scheduleBlink();
        });
      }, delay);
    };

    void preloadImageUrls([speakingHalfBlinkUrl, speakingClosedBlinkUrl]);
    applyBlinkPhase("open");
    scheduleBlink(firstBlinkDelayMs);

    return () => {
      cancelled = true;
      window.clearTimeout(blinkTimerRef.current);
      blinkSequenceRef.current += 1;
      applyBlinkPhase("open");
    };
  }, [
    applyBlinkPhase,
    debugPlaybackRate,
    doSpeakingBlink,
    eyeMotion,
    playbackClockRef,
    playbackMode,
    speakingClosedBlinkUrl,
    speakingHalfBlinkUrl,
  ]);

  const imgSrc = resolveImageSrc(displayImage) ?? closedFallback;
  const fadeFromSrc = resolveImageSrc(fadeFromImage);
  const headAnimationStyle = { animationDelay: `${headAnimationDelayMs}ms` };
  const speakingBlinkMaskStyle = eyeMotion ? {
    maskImage: eyeMotion.maskImage ?? SPEAKING_BLINK_MASK,
    WebkitMaskImage: eyeMotion.maskImage ?? SPEAKING_BLINK_MASK,
    maskMode: eyeMotion.maskMode,
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskPosition: eyeMotion.maskPosition,
    WebkitMaskPosition: eyeMotion.maskPosition,
    maskSize: eyeMotion.maskSize,
    WebkitMaskSize: eyeMotion.maskSize,
  } : undefined;
  const renderCrossfadeStack = (style?: CSSProperties) => (
    <>
      {fadeFromSrc && (
        <img
          src={fadeFromSrc}
          alt=""
          className="w-full h-full object-cover absolute inset-0"
          style={style}
          onError={() => handleImageLoadError(fadeFromImage)}
        />
      )}
      <img
        src={imgSrc}
        alt="avatar"
        className="w-full h-full object-cover absolute inset-0"
        style={{
          ...style,
          opacity: displayOpacity,
          transition: `opacity ${displayTransitionMs}ms ease-out`,
        }}
        onError={() => handleImageLoadError(displayImage)}
      />
    </>
  );
  const playbackIssueOverlay = playbackIssue ? (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/65 px-6 text-white">
      <div className="max-w-sm rounded-lg border border-white/20 bg-black/75 px-5 py-4 text-center shadow-xl">
        <p className="text-sm font-bold">{describePlaybackIssue(playbackIssue)}</p>
        <p className="mt-1 break-all text-xs text-white/70">{playbackIssue.reason}</p>
        <button
          type="button"
          onClick={retryAudioPlayback}
          className="mt-4 inline-flex h-10 items-center justify-center gap-2 rounded-md bg-cyan-600 px-4 text-sm font-bold text-white shadow hover:bg-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-200"
        >
          <Play className="h-4 w-4" />
          再生を開始
        </button>
      </div>
    </div>
  ) : null;

  if (renderMode === "canvas") {
    return (
      <div className="relative w-full h-full overflow-hidden">
        <canvas
          ref={canvasRef}
          width={canvasInternalWidth}
          height={canvasInternalHeight}
          className="h-full w-full"
          style={{ display: "block" }}
          aria-label="avatar"
          role="img"
        />
        {playbackIssueOverlay}
        {debug && inlineDebugOverlay && debugSnapshot && (
          <div className="pointer-events-none absolute left-2 right-2 top-2 z-20 rounded bg-black/80 px-2 py-1 text-left font-mono text-[10px] leading-tight text-white shadow">
            <div className="break-all">runtime: {debugSnapshot.runtimePackRoot ?? "-"}</div>
            <div>azure: {debugSnapshot.azureVisemeId ?? "-"} / pose: {debugSnapshot.dentalPose ?? "-"}</div>
            <div>target: {debugSnapshot.targetAssetSource} / {debugSnapshot.targetImage}</div>
            <div>legacy: {debugSnapshot.legacyViseme ?? "-"} / frame: {debugSnapshot.assetSource} / {debugSnapshot.displayImage}</div>
            <div>asset: {debugSnapshot.assetSource} / transition: canvas</div>
            <div>
              trans: {debugSnapshot.transitionLoadedCount ?? "-"} loaded / preload: {String(debugSnapshot.transitionPreloadComplete ?? false)}
              {" "} / canvas: {String(debugSnapshot.canvasImagesReady ?? false)}
            </div>
            <div>
              queue: {debugSnapshot.displayQueueLength ?? "-"} / target: {debugSnapshot.activeTransitionTarget ?? "-"}
              {" "} / min: {debugSnapshot.minDisplayFrameMs ?? "-"}ms
            </div>
            <div>
              canvasFps: {debugSnapshot.canvasFps ?? "-"} / renders: {debugSnapshot.canvasRenderCount ?? "-"}
              {" "} / dt: {debugSnapshot.canvasRenderDeltaMs ?? "-"}ms
            </div>
            <div>
              jaw: {debugSnapshot.jawOpen} / y: {debugSnapshot.jawTranslateY}% / scaleY: {debugSnapshot.jawScaleY}
            </div>
            <div>rate: {debugSnapshot.playbackRate} / t: {debugSnapshot.timeMs}ms / status: {debugSnapshot.status ?? "-"}</div>
            <div className="break-all">url: {debugSnapshot.assetUrl}</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* 固定ベース: 頭部マスク外の背景・首・肩を残す */}
      <img
        src={baseImageUrl}
        alt=""
        className="w-full h-full object-cover absolute inset-0"
      />
      <div
        className="absolute inset-0 avatar-head"
        style={{
          maskImage: HEAD_MOTION_MASK,
          WebkitMaskImage: HEAD_MOTION_MASK,
          ...headAnimationStyle,
        }}
      >
        {/* ベースレイヤー: closed(base)画像を全体表示。jawレイヤーが口を上書きする */}
        <img
          src={baseImageUrl}
          alt=""
          className="w-full h-full object-cover absolute inset-0"
        />
        {/* 顎だけ別レイヤでわずかに下げる。首・肩はベース画像側に残す */}
        <div className="absolute inset-0">
          <div
            ref={jawLayerRef}
            className="absolute inset-0"
            style={{
              maskImage: jawMotionConfig.maskImage,
              WebkitMaskImage: jawMotionConfig.maskImage,
              maskMode: "luminance",
              maskRepeat: "no-repeat",
              WebkitMaskRepeat: "no-repeat",
              maskSize: "100% 100%",
              WebkitMaskSize: "100% 100%",
              maskPosition: "center",
              WebkitMaskPosition: "center",
              transformOrigin: jawMotionConfig.transformOrigin,
              transform: "translateY(0%) scaleX(1) scaleY(1)",
              transition: `transform ${jawMotionConfig.transitionMs}ms ease-out, opacity ${displayTransitionMs}ms ease-out`,
              willChange: "transform",
            }}
          >
            {renderCrossfadeStack()}
          </div>
        </div>
        {eyeMotion && speakingHalfBlinkUrl && speakingClosedBlinkUrl && (
          <div
            className="pointer-events-none absolute inset-0"
            style={speakingBlinkMaskStyle}
          >
            <img
              ref={halfBlinkRef}
              src={speakingHalfBlinkUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                opacity: 0,
                transition: `opacity ${eyeMotion.transitionMs ?? 60}ms ease-out`,
              }}
            />
            <img
              ref={closedBlinkRef}
              src={speakingClosedBlinkUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{
                opacity: 0,
                transition: `opacity ${eyeMotion.transitionMs ?? 60}ms ease-out`,
              }}
            />
          </div>
        )}
      </div>
      {debug && debugSnapshot && (
        <div className="pointer-events-none absolute left-2 right-2 top-2 z-20 rounded bg-black/80 px-2 py-1 text-left font-mono text-[10px] leading-tight text-white shadow">
          <div className="break-all">runtime: {debugSnapshot.runtimePackRoot ?? "-"}</div>
          <div>azure: {debugSnapshot.azureVisemeId ?? "-"} / pose: {debugSnapshot.dentalPose ?? "-"}</div>
          <div>target: {debugSnapshot.targetAssetSource} / {debugSnapshot.targetImage}</div>
          <div>legacy: {debugSnapshot.legacyViseme ?? "-"} / frame: {debugSnapshot.assetSource} / {debugSnapshot.displayImage}</div>
          <div>asset: {debugSnapshot.assetSource} / transition: {debugSnapshot.transitionMode}</div>
          <div>
            trans: {debugSnapshot.transitionLoadedCount ?? "-"} loaded / preload: {String(debugSnapshot.transitionPreloadComplete ?? false)}
            {" "} / canvas: {String(debugSnapshot.canvasImagesReady ?? false)}
          </div>
          <div>
            queue: {debugSnapshot.displayQueueLength ?? "-"} / target: {debugSnapshot.activeTransitionTarget ?? "-"}
            {" "} / min: {debugSnapshot.minDisplayFrameMs ?? "-"}ms
          </div>
          <div>
            canvasFps: {debugSnapshot.canvasFps ?? "-"} / renders: {debugSnapshot.canvasRenderCount ?? "-"}
            {" "} / dt: {debugSnapshot.canvasRenderDeltaMs ?? "-"}ms
          </div>
          <div>
            jaw: {debugSnapshot.jawOpen} / y: {debugSnapshot.jawTranslateY}% / scaleY: {debugSnapshot.jawScaleY}
          </div>
          <div>rate: {debugSnapshot.playbackRate} / t: {debugSnapshot.timeMs}ms / status: {debugSnapshot.status ?? "-"}</div>
          <div className="break-all">url: {debugSnapshot.assetUrl}</div>
        </div>
      )}
      {playbackIssueOverlay}
    </div>
  );
};

export default VisemePlayer;
